import AcademicYearLockNotice from '../components/common/AcademicYearLockNotice';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabaseClient';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { saveBudgetNoteWorkspace } from '../services/budgetNoteService';
import { useBudgetNoteWorkspaceQuery } from '../query/hooks/useBudgetNoteWorkspaceQuery';
import type { BudgetNoteRowDraft, BudgetNoteUsageEntryDraft, BudgetUsageKind } from '../types/budgetNote';
import BudgetNoteUsageDateField, { type BudgetUsageDueKind } from '../components/budgetNote/BudgetNoteUsageDateField';
import { toISODate } from '../utils/date';
import { nextBudgetSuppApplyRound } from '../utils/budgetSuppApplyHistory';
import ProgramColorPickModal, {
  type ProgramColorModalSubmitPayload,
} from '../components/ProgramColorPickModal';
import {
  BUDGET_META_COLOR_FIELD,
  BUDGET_META_COL_KEYS,
  budgetMetaCellKey,
  budgetMetaCellNeighborKeys,
  parseBudgetMetaCellKey,
  rectBudgetMetaCellKeys,
  type BudgetMetaColKey,
} from '../utils/budgetNoteMetaSelection';
import {
  budgetMarkColorAriaLabel,
  budgetMarkMemoFillStyle,
  collectUsedBudgetMarkCustomColors,
  DEFAULT_BUDGET_NOTE_MARK_COLORS,
  isBudgetMarkThemeInk,
  normalizeBudgetMarkHex,
} from '../utils/budgetNoteMarkColors';
import { budgetAmountDigitsToHangulWon } from '../utils/budgetAmountHangul';
import DeleteHoldButton from '../components/common/DeleteHoldButton';
import {
  BUDGET_CATEGORY_KEYS,
  DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS,
  normalizeBudgetNoteCategoryHeaderLabels,
  type BudgetCategoryKey,
  type BudgetNoteCategoryHeaderLabels,
} from '../utils/budgetNoteCategoryHeaderLabels';
import {
  BudgetNoteSearchHighlightedText,
  BudgetNoteSearchMirroredInput,
} from '../components/budgetNote/BudgetNoteSearchHighlight';
import {
  buildBudgetMetaSearchMatchId,
  buildBudgetUsageSearchMatchId,
  collectBudgetNoteSearchMatches,
  normalizeBudgetSearchQuery,
  textIncludesBudgetSearchQuery,
} from '../utils/budgetNoteSearch';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import {
  BUDGET_NOTE_UNDO_COALESCE_MS,
  BUDGET_NOTE_UNDO_HISTORY_MAX,
  cloneBudgetNoteHistoryFrame,
  type BudgetNoteHistoryFrame,
} from '../utils/budgetNoteUndoHistory';
import { computeZoomAwareFixedPopupStyle, PAGE_ZOOM_CHANGED_EVENT } from '../utils/pageZoom';

function generateId(): string {
  return crypto.randomUUID();
}

function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('ko-KR');
}

/** 추경증감 입력식: `-2000+5000` 처럼 +/- 로 이은 항을 합산 (▲=+, ▼=-). 숫자 항이 없으면 null */
function evaluateSignedDeltaInput(raw: string): number | null {
  const expr = raw.replace(/▲/g, '+').replace(/▼/g, '-').replace(/[,\s]/g, '');
  let sum = 0;
  let found = false;
  for (const m of Array.from(expr.matchAll(/([+-]*)(\d+)/g))) {
    const minusCount = (m[1].match(/-/g) ?? []).length;
    const n = Math.min(Number.MAX_SAFE_INTEGER, Number(m[2]));
    sum += minusCount % 2 === 1 ? -n : n;
    found = true;
  }
  return found ? sum : null;
}

/** 편집 중 원문: 숫자·+/-·▲▼ 만 남기고 각 숫자 항에 천 단위 콤마 (예: `4,000+2,000`) */
function formatSignedDeltaDraft(raw: string): string {
  return raw
    .replace(/[^\d,+\-▲▼]/g, '')
    .replace(/[\d,]+/g, (m) => {
      const d = m.replace(/,/g, '');
      return d === '' ? '' : Math.min(Number.MAX_SAFE_INTEGER, Number(d)).toLocaleString('ko-KR');
    });
}

/** 콤마를 뺀 의미 있는 글자 n개 뒤의 위치 (콤마 재배치 후 커서 복원용) */
function caretAfterSignificantChars(text: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== ',') seen += 1;
    if (seen >= count) return i + 1;
  }
  return text.length;
}

/** 숫자 하나(부호 포함)가 아니라 +/- 로 이은 계산식인지 */
function isSignedDeltaFormula(raw: string): boolean {
  return /\d[\d,\s]*[+\-▲▼]\s*\d/.test(raw);
}

/** 추경증감 셀: 평소엔 ▲/▼ 표시, 포커스 시 저장된 계산식(결과가 일치할 때) 또는 부호 숫자로 편집 */
const BudgetSupplementDeltaInput: React.FC<{
  delta: number | null;
  formula?: string | null;
  className: string;
  onChangeDelta: (delta: number | null, formula: string | null) => void;
}> = ({ delta, formula, className, onChangeDelta }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const caret = pendingCaretRef.current;
    const el = inputRef.current;
    if (caret === null || !el || document.activeElement !== el) return;
    pendingCaretRef.current = null;
    el.setSelectionRange(caret, caret);
  }, [draft]);
  const rounded = delta === null || !Number.isFinite(delta) ? null : Math.round(delta);
  const direction = rounded === null ? '' : rounded === 0 ? 'zero' : rounded > 0 ? 'up' : 'down';
  const idleText =
    rounded === null ? '' : rounded === 0 ? '0' : `${rounded > 0 ? '▲' : '▼'} ${formatMoney(Math.abs(rounded))}`;
  const savedFormulaText = (formula ?? '').trim();
  const activeFormula =
    rounded !== null && savedFormulaText !== '' && evaluateSignedDeltaInput(savedFormulaText) === rounded
      ? formatSignedDeltaDraft(savedFormulaText)
      : null;
  const [formulaPopStyle, setFormulaPopStyle] = useState<CSSProperties | null>(null);
  const showFormulaPop = !!formulaPopStyle && !!activeFormula && draft === null;
  return (
    <>
    <input
      ref={inputRef}
      className={`${className} cp-budget-note-delta-input${direction ? ` cp-budget-note-delta-input--${direction}` : ''}${
        activeFormula ? ' cp-budget-note-delta-input--has-formula' : ''
      }`}
      inputMode="numeric"
      value={draft ?? idleText}
      onMouseEnter={(e) => {
        if (!activeFormula) return;
        setFormulaPopStyle(
          computeZoomAwareFixedPopupStyle({
            anchorRect: e.currentTarget.getBoundingClientRect(),
            preferredWidth: 160,
            estimatedHeight: 40,
            gap: 4,
            pad: 8,
            zIndex: 96,
            placement: 'auto',
            alignX: 'center',
          })
        );
      }}
      onMouseLeave={() => setFormulaPopStyle(null)}
      onMouseDown={() => setFormulaPopStyle(null)}
      onFocus={(e) => {
        const savedFormula = (formula ?? '').trim();
        const formulaMatches =
          rounded !== null && savedFormula !== '' && evaluateSignedDeltaInput(savedFormula) === rounded;
        setDraft(formatSignedDeltaDraft(formulaMatches ? savedFormula : rounded === null ? '' : String(rounded)));
        const el = e.currentTarget;
        requestAnimationFrame(() => el.select());
      }}
      onChange={(e) => {
        const typed = e.target.value;
        const caret = e.target.selectionStart ?? typed.length;
        const significantBeforeCaret = typed
          .slice(0, caret)
          .replace(/[^\d+\-▲▼]/g, '').length;
        const formatted = formatSignedDeltaDraft(typed);
        pendingCaretRef.current = caretAfterSignificantChars(formatted, significantBeforeCaret);
        setDraft(formatted);
        const value = evaluateSignedDeltaInput(formatted);
        onChangeDelta(value, value !== null && isSignedDeltaFormula(formatted) ? formatted : null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      onBlur={() => setDraft(null)}
      aria-label={activeFormula ? `추경증감, 계산식 ${activeFormula}` : '추경증감 (감소는 -, 계산식 입력 가능)'}
      title={
        activeFormula
          ? undefined
          : '증가는 숫자, 감소는 - 를 붙여 입력합니다. -2000+5000 처럼 계산식도 됩니다. 추경 후 예산이 자동 계산됩니다'
      }
    />
    {showFormulaPop
      ? createPortal(
          <div className="cp-budget-note-formula-pop" role="tooltip" style={formulaPopStyle ?? undefined}>
            <span className="cp-budget-note-formula-pop-label">수식</span>
            <span className="cp-budget-note-formula-pop-expr">{activeFormula}</span>
          </div>,
          document.body
        )
      : null}
    </>
  );
};

type BudgetNumColKey = 'cur' | 'supp' | 'delta' | 'proj' | 'used' | 'bal' | 'suppbal';
type BudgetNumCellPos = { row: number; col: BudgetNumColKey };

/** 금액 열 셀 값 (표와 동일 계산). 추경 미입력 칸은 null */
function budgetNumCellValue(
  col: BudgetNumColKey,
  v: { cur: number; supp: number | null; proj: number; used: number; plannedInBalance: number }
): number | null {
  switch (col) {
    case 'cur':
      return v.cur;
    case 'supp':
      return v.supp;
    case 'delta':
      return v.supp === null ? null : v.supp - v.cur;
    case 'proj':
      return v.proj;
    case 'used':
      return v.used + v.plannedInBalance;
    case 'bal':
      return v.proj - v.used - v.plannedInBalance;
    case 'suppbal':
      return v.supp === null ? null : v.supp - v.used - v.plannedInBalance;
    default:
      return null;
  }
}

/** 추경잔액: 메인 초록 볼드, 초과 사용(음수)은 빨강 */
function renderSupplementBalance(balance: number | null): React.ReactNode {
  if (balance === null) return null;
  const rounded = Number.isFinite(balance) ? Math.round(balance) : 0;
  return (
    <span className={`cp-budget-note-suppbal-value${rounded < 0 ? ' cp-budget-note-suppbal-value--negative' : ''}`}>
      {formatMoney(rounded)}
    </span>
  );
}

/** 사업예산(전체) 대비 현재잔액 비율 0~1 — 잔액 게이지 너비용 */
function budgetBalanceRatio(projectBudget: number, currentBalance: number): number {
  if (!(projectBudget > 0) || !Number.isFinite(currentBalance)) return 0;
  return Math.max(0, Math.min(1, currentBalance / projectBudget));
}

function renderBudgetBalanceGauge(projectBudget: number, currentBalance: number) {
  const pct = Math.round(budgetBalanceRatio(projectBudget, currentBalance) * 1000) / 10;
  return (
    <div className="cp-budget-note-bal-gauge" aria-hidden>
      <div className="cp-budget-note-bal-gauge__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function parseMoneyInput(raw: string): number {
  const s = raw.replace(/,/g, '').replace(/\s/g, '').trim();
  if (s === '' || s === '-') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

type BudgetNotePasteColKey = BudgetMetaColKey | 'current_budget' | 'supp_budget' | 'project_budget';

function shouldHandleBudgetBulkPaste(text: string): boolean {
  if (text.includes('\t')) return true;
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim());
  return lines.length >= 2;
}

function parseBudgetNotePasteGrid(raw: string): string[][] {
  const text = raw.replace(/^\uFEFF/, '');
  const lines = text.split(/\r\n|\n|\r/);
  const grid = lines.map((line) => line.split('\t').map((cell) => cell.replace(/\r$/, '')));
  while (grid.length && grid[grid.length - 1].every((cell) => !(cell ?? '').trim())) {
    grid.pop();
  }
  return grid;
}

function getBudgetPasteableColumnKeys(
  metaColsVisible: Record<BudgetMetaColKey, boolean>,
  suppDataVisible: boolean
): BudgetNotePasteColKey[] {
  const cols: BudgetNotePasteColKey[] = [];
  if (metaColsVisible.cat1) cols.push('cat1');
  if (metaColsVisible.cat2) cols.push('cat2');
  if (metaColsVisible.cat3) cols.push('cat3');
  if (metaColsVisible.cat4) cols.push('cat4');
  if (metaColsVisible.memo) cols.push('memo');
  cols.push('current_budget');
  if (suppDataVisible) cols.push('supp_budget');
  cols.push('project_budget');
  return cols;
}

function getBudgetSelectableMetaCols(metaColsVisible: Record<BudgetMetaColKey, boolean>): BudgetMetaColKey[] {
  return BUDGET_META_COL_KEYS.filter((k) => metaColsVisible[k]);
}

function budgetPasteCellToPatch(key: BudgetNotePasteColKey, raw: string): Partial<BudgetNoteRowDraft> {
  const v = raw.trim();
  switch (key) {
    case 'cat1':
      return { cat1: v };
    case 'cat2':
      return { cat2: v };
    case 'cat3':
      return { cat3: v };
    case 'cat4':
      return { cat4: v };
    case 'memo':
      return { memo: v };
    case 'current_budget':
      return { current_budget: parseMoneyInput(v) };
    case 'supp_budget':
      return { supp_budget: v === '' ? null : parseMoneyInput(v) };
    case 'project_budget':
      return { project_budget: parseMoneyInput(v) };
    default:
      return {};
  }
}

function normalizeWorkspace(
  rows: BudgetNoteRowDraft[],
  entries: BudgetNoteUsageEntryDraft[],
  deletedRowIds: string[],
  deletedEntryIds: string[],
  categoryHeaderLabels: BudgetNoteCategoryHeaderLabels
) {
  const rs = [...rows].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  const es = [...entries].sort(
    (a, b) => a.row_id.localeCompare(b.row_id) || a.sort_order - b.sort_order || a.id.localeCompare(b.id)
  );
  const dr = [...deletedRowIds].sort();
  const de = [...deletedEntryIds].sort();
  const labels = normalizeBudgetNoteCategoryHeaderLabels(categoryHeaderLabels);
  return JSON.stringify({ rows: rs, entries: es, deletedRowIds: dr, deletedEntryIds: de, categoryHeaderLabels: labels });
}

function emptyRow(userId: string, sortOrder: number, workspaceId: string | null): BudgetNoteRowDraft {
  return {
    id: generateId(),
    user_id: userId,
    workspace_id: workspaceId,
    sort_order: sortOrder,
    cat1: '',
    cat2: '',
    cat3: '',
    cat4: '',
    memo: '',
    cat1_color: '',
    cat2_color: '',
    cat3_color: '',
    cat4_color: '',
    memo_color: '',
    current_budget: 0,
    supp_budget: null,
    project_budget: 0,
    is_hidden: false,
  };
}

type BudgetCategorySortDir = 'asc' | 'desc';

const DEFAULT_CATEGORY_SORT: Record<BudgetCategoryKey, BudgetCategorySortDir> = {
  cat1: 'asc',
  cat2: 'asc',
  cat3: 'asc',
  cat4: 'asc',
};

function categorySortAriaLabel(label: string, dir: BudgetCategorySortDir): string {
  if (dir === 'asc') return `${label} 오름차순, 클릭하면 내림차순`;
  return `${label} 내림차순, 클릭하면 오름차순`;
}

const BUDGET_NOTE_DELETE_HOLD_MS = 820;

/** 가로모드: 카테고리1~4 열 기본 너비(px) — 집행 열에 공간 양보 */
const BUDGET_H_CATEGORY_COL_MIN_PX = 64;
/** 가로모드: 메모 열 기본 너비(px) */
const BUDGET_H_MEMO_COL_MIN_PX = 72;
/** 가로모드: 현재예산~추경잔액 열 고정 너비(px) — 천만원(10,000,000)까지 표시 */
const BUDGET_H_METRIC_COL_PX = 96;
/** 가로모드: 행 메뉴 열 너비(px) */
const BUDGET_H_ROWACT_COL_PX = 26;
/** 집행 블럭 너비(px) — CSS --cp-budget-note-usage-block-w (7.75rem * 3/5) 와 맞춤 */
const BUDGET_H_USAGE_BLOCK_PX = Math.round((7.75 * 16 * 3) / 5);
/** 집행 열 최소 너비: +버튼 1 + 예산블럭 5 정도 보이게 */
const BUDGET_H_USAGE_COL_MIN_PX = BUDGET_H_USAGE_BLOCK_PX * 6 + 4 * 5 + 8;

const BUDGET_H_META_COL_WIDTHS_STORAGE_KEY = 'curriculumPlanner.budgetNote.horizontalMetaColWidthsPx.v1';
const DEFAULT_META_COL_WIDTHS_PX: Record<BudgetMetaColKey, number> = {
  cat1: BUDGET_H_CATEGORY_COL_MIN_PX,
  cat2: BUDGET_H_CATEGORY_COL_MIN_PX,
  cat3: BUDGET_H_CATEGORY_COL_MIN_PX,
  cat4: BUDGET_H_CATEGORY_COL_MIN_PX,
  memo: BUDGET_H_MEMO_COL_MIN_PX,
};
const BUDGET_H_META_COL_RESIZE_MIN_PX = 52;
const BUDGET_H_META_COL_RESIZE_MAX_PX = 400;

/** 추경증감 열: 계산식 입력용 가로 조절 (가로 모드) */
const BUDGET_H_DELTA_COL_WIDTH_STORAGE_KEY = 'curriculumPlanner.budgetNote.horizontalDeltaColWidthPx.v1';
const BUDGET_H_DELTA_COL_RESIZE_MIN_PX = 64;
const BUDGET_H_DELTA_COL_RESIZE_MAX_PX = 400;

function clampDeltaColWidthPx(v: unknown): number | null {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return null;
  return Math.min(BUDGET_H_DELTA_COL_RESIZE_MAX_PX, Math.max(BUDGET_H_DELTA_COL_RESIZE_MIN_PX, n));
}

function loadStoredDeltaColWidthPx(): number {
  try {
    const raw = localStorage.getItem(BUDGET_H_DELTA_COL_WIDTH_STORAGE_KEY);
    if (!raw) return BUDGET_H_METRIC_COL_PX;
    return clampDeltaColWidthPx(raw) ?? BUDGET_H_METRIC_COL_PX;
  } catch {
    return BUDGET_H_METRIC_COL_PX;
  }
}

function loadStoredMetaColWidthsPx(): Record<BudgetMetaColKey, number> {
  try {
    const raw = localStorage.getItem(BUDGET_H_META_COL_WIDTHS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_META_COL_WIDTHS_PX };
    const parsed = JSON.parse(raw) as Partial<Record<BudgetMetaColKey, number>>;
    const clamp = (v: unknown) => {
      const n = Math.round(Number(v));
      if (!Number.isFinite(n)) return null;
      return Math.min(BUDGET_H_META_COL_RESIZE_MAX_PX, Math.max(BUDGET_H_META_COL_RESIZE_MIN_PX, n));
    };
    const out = { ...DEFAULT_META_COL_WIDTHS_PX };
    for (const k of BUDGET_META_COL_KEYS) {
      const c = clamp(parsed[k]);
      if (c != null) out[k] = c;
    }
    return out;
  } catch {
    return { ...DEFAULT_META_COL_WIDTHS_PX };
  }
}

function BudgetNoteUsageFlyPopBody({
  entry,
  searchQuery,
  activeMatchId,
}: {
  entry: BudgetNoteUsageEntryDraft;
  searchQuery: string;
  activeMatchId: string | null;
}) {
  return (
    <>
      <span className="cp-budget-note-block-pop-line cp-budget-note-block-pop-line--kind">
        {entry.kind === 'planned' ? '집행예정' : '집행완료'}
      </span>
      <span className="cp-budget-note-block-pop-line">{entry.usage_date}</span>
      <span className="cp-budget-note-block-pop-line cp-budget-note-block-pop-line--amt">
        {formatMoney(Number(entry.amount) || 0)}
      </span>
      {entry.memo ? (
        <span className="cp-budget-note-block-pop-memo">
          <BudgetNoteSearchHighlightedText
            text={entry.memo}
            query={searchQuery}
            activeMatchId={activeMatchId}
            buildMatchId={(start) => buildBudgetUsageSearchMatchId(entry.id, start)}
          />
        </span>
      ) : null}
    </>
  );
}

const BUDGET_META_COL_LABELS: Record<BudgetMetaColKey, string> = {
  cat1: DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS.cat1,
  cat2: DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS.cat2,
  cat3: DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS.cat3,
  cat4: DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS.cat4,
  memo: '메모',
};

function cmpCategoryLocale(a: string, b: string, dir: 'asc' | 'desc'): number {
  const va = (a ?? '').trim();
  const vb = (b ?? '').trim();
  const aEmpty = va === '';
  const bEmpty = vb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const base = va.localeCompare(vb, 'ko', { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? base : -base;
}

/** cat1~4 기준 정렬 (전역 필터 ON일 때만 적용, sort_order는 보존) */
function sortRowsByCategorySort(
  rs: BudgetNoteRowDraft[],
  filterEnabled: boolean,
  sort: Record<BudgetCategoryKey, BudgetCategorySortDir>
): BudgetNoteRowDraft[] {
  const base = [...rs].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  if (!filterEnabled) return base;
  return [...base].sort((a, b) => {
    for (const key of BUDGET_CATEGORY_KEYS) {
      const r = cmpCategoryLocale(a[key], b[key], sort[key]);
      if (r !== 0) return r;
    }
    return a.sort_order - b.sort_order || a.id.localeCompare(b.id);
  });
}

/** 저장 시 필터가 켜져 있으면 정렬 결과로 sort_order 재부여 */
function applyActiveCategorySortToRows(
  rs: BudgetNoteRowDraft[],
  filterEnabled: boolean,
  sort: Record<BudgetCategoryKey, BudgetCategorySortDir>
): BudgetNoteRowDraft[] {
  return sortRowsByCategorySort(rs, filterEnabled, sort).map((row, i) => ({ ...row, sort_order: i }));
}

const BUDGET_LAYOUT_MODE_STORAGE_KEY = 'curriculumPlanner.budgetNote.layoutMode.v1';
/** 행 메뉴 미니모달 예상 높이(첫 측정 전 flip 판단용) */
const BUDGET_ROW_MENU_EST_HEIGHT_PX = 44;
const BUDGET_ROW_MENU_GAP_PX = 4;

type BudgetRowMenuState = {
  rowId: string;
  left: number;
  top: number;
  anchorTop: number;
  anchorBottom: number;
};

function buildBudgetRowMenuState(
  rowId: string,
  triggerEl: HTMLElement,
  menuHeightPx = BUDGET_ROW_MENU_EST_HEIGHT_PX
): BudgetRowMenuState {
  const r = triggerEl.getBoundingClientRect();
  const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 600;
  const left = Math.max(8, Math.min(r.left, vw - 120));
  const spaceBelow = vh - r.bottom - BUDGET_ROW_MENU_GAP_PX;
  const placeAbove = spaceBelow < menuHeightPx + 8;
  const top = placeAbove
    ? Math.max(8, r.top - menuHeightPx - BUDGET_ROW_MENU_GAP_PX)
    : r.bottom + BUDGET_ROW_MENU_GAP_PX;
  return { rowId, left, top, anchorTop: r.top, anchorBottom: r.bottom };
}

/** 예산노트 보기 옵션(필터·정렬 방향·숨김/추경 보이기) — 기기별 유지 */
const BUDGET_VIEW_OPTIONS_STORAGE_KEY = 'curriculumPlanner.budgetNote.viewOptions.v1';

type BudgetViewOptions = {
  suppDataVisible: boolean;
  hiddenDataVisible: boolean;
  plannedInBalanceEnabled: boolean;
  categorySortFilterEnabled: boolean;
  categorySort: Record<BudgetCategoryKey, BudgetCategorySortDir>;
};

function loadStoredBudgetViewOptions(): BudgetViewOptions {
  const fallback: BudgetViewOptions = {
    suppDataVisible: false,
    hiddenDataVisible: false,
    plannedInBalanceEnabled: false,
    categorySortFilterEnabled: false,
    categorySort: { ...DEFAULT_CATEGORY_SORT },
  };
  try {
    const raw = localStorage.getItem(BUDGET_VIEW_OPTIONS_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<BudgetViewOptions>;
    const sort = { ...DEFAULT_CATEGORY_SORT };
    for (const k of Object.keys(sort) as BudgetCategoryKey[]) {
      const dir = parsed.categorySort?.[k];
      if (dir === 'asc' || dir === 'desc') sort[k] = dir;
    }
    return {
      suppDataVisible: parsed.suppDataVisible === true,
      hiddenDataVisible: parsed.hiddenDataVisible === true,
      plannedInBalanceEnabled: parsed.plannedInBalanceEnabled === true,
      categorySortFilterEnabled: parsed.categorySortFilterEnabled === true,
      categorySort: sort,
    };
  } catch {
    return fallback;
  }
}

function loadStoredBudgetLayoutMode(): 'horizontal' | 'vertical' {
  try {
    const v = localStorage.getItem(BUDGET_LAYOUT_MODE_STORAGE_KEY);
    if (v === 'vertical') return 'vertical';
    return 'horizontal';
  } catch {
    return 'horizontal';
  }
}

const BUDGET_META_COLS_VISIBLE_STORAGE_KEY = 'curriculumPlanner.budgetNote.metaColsVisible.v1';

const DEFAULT_META_COLS_VISIBLE: Record<BudgetMetaColKey, boolean> = {
  cat1: true,
  cat2: true,
  cat3: true,
  cat4: true,
  memo: true,
};

function loadStoredMetaColsVisible(): Record<BudgetMetaColKey, boolean> {
  try {
    const raw = localStorage.getItem(BUDGET_META_COLS_VISIBLE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_META_COLS_VISIBLE };
    const parsed = JSON.parse(raw) as Partial<Record<BudgetMetaColKey, unknown>>;
    return {
      ...DEFAULT_META_COLS_VISIBLE,
      cat1: typeof parsed.cat1 === 'boolean' ? parsed.cat1 : DEFAULT_META_COLS_VISIBLE.cat1,
      cat2: typeof parsed.cat2 === 'boolean' ? parsed.cat2 : DEFAULT_META_COLS_VISIBLE.cat2,
      cat3: typeof parsed.cat3 === 'boolean' ? parsed.cat3 : DEFAULT_META_COLS_VISIBLE.cat3,
      cat4: typeof parsed.cat4 === 'boolean' ? parsed.cat4 : DEFAULT_META_COLS_VISIBLE.cat4,
      memo: typeof parsed.memo === 'boolean' ? parsed.memo : DEFAULT_META_COLS_VISIBLE.memo,
    };
  } catch {
    return { ...DEFAULT_META_COLS_VISIBLE };
  }
}

function normalizeUsageSortDate(raw: string, fallbackIso: string): string {
  const d = (raw ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return fallbackIso;
}

/** 세로 모드 집행: 미래·당일은 날짜 내림차순(더 먼 미래가 위), 과거는 내림차순(최근 과거가 위) */
function compareBudgetUsageForVertical(
  a: BudgetNoteUsageEntryDraft,
  b: BudgetNoteUsageEntryDraft,
  todayIso: string
): number {
  const da = normalizeUsageSortDate(a.usage_date, todayIso);
  const db = normalizeUsageSortDate(b.usage_date, todayIso);
  const aPast = da < todayIso;
  const bPast = db < todayIso;
  if (aPast !== bPast) return aPast ? 1 : -1;
  if (!aPast && !bPast) return db.localeCompare(da);
  return db.localeCompare(da);
}

/** 모달 금액 필드: 숫자만 저장, 화면에는 천 단위 콤마 */
function usageAmountDigitsFromRaw(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d === '') return '';
  const n = Number(d);
  if (!Number.isFinite(n) || n < 0) return '';
  return String(Math.min(Number.MAX_SAFE_INTEGER, Math.round(n)));
}

function usageAmountDisplayFromDigits(digits: string): string {
  if (digits === '') return '';
  const n = Number(digits);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('ko-KR');
}

/** 예상 잔액 필드: 음수 허용, 입력 중 원문은 '-' 와 콤마 표시만 정리 */
function usageBalanceDisplayFromRaw(raw: string): string {
  const negative = raw.trim().startsWith('-');
  const d = raw.replace(/\D/g, '');
  if (d === '') return negative ? '-' : '';
  const n = Math.min(Number.MAX_SAFE_INTEGER, Number(d));
  return (negative ? '-' : '') + n.toLocaleString('ko-KR');
}

function usageBalanceValueFromRaw(raw: string): number | null {
  const d = raw.replace(/\D/g, '');
  if (d === '') return null;
  const n = Math.min(Number.MAX_SAFE_INTEGER, Number(d));
  return raw.trim().startsWith('-') ? -n : n;
}

const BudgetUsageDeleteHoldButton: React.FC<{
  entryId: string;
  onRemove: () => void;
}> = ({ entryId, onRemove }) => (
  <DeleteHoldButton
    key={entryId}
    onDelete={onRemove}
    holdMs={BUDGET_NOTE_DELETE_HOLD_MS}
    className="cp-budget-note-delete-hold-btn"
    holdBarClassName="cp-budget-note-delete-hold-bar"
    title="짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제"
    ariaLabel="이 집행 내역 삭제 (짧게: 확인, 길게: 바로 삭제)"
    confirmTitle="이 집행 내역을 삭제할까요?"
    confirmDescription="삭제하면 되돌릴 수 없습니다."
  />
);

/** 행 메뉴·복제 등: 길게 눌러 게이지 완료 시 동작. 짧은 클릭은 onShortPress(있으면) */
const BudgetNoteHoldActionButton: React.FC<{
  actionKey: string;
  onComplete: () => void;
  /** 길게 누르기 전에 손을 떼면 호출(삭제 확인 등) */
  onShortPress?: () => void;
  ariaLabel: string;
  title?: string;
  className?: string;
  'data-tutorial-bn'?: string;
  children: React.ReactNode;
}> = ({ actionKey, onComplete, onShortPress, ariaLabel, title, className, children, ...rest }) => {
  const [holdProgress, setHoldProgress] = useState(0);
  const holdStartRef = useRef<number | null>(null);
  const holdRafRef = useRef<number | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const onCompleteRef = useRef(onComplete);
  const onShortPressRef = useRef(onShortPress);
  onCompleteRef.current = onComplete;
  onShortPressRef.current = onShortPress;

  const cancelHold = useCallback(() => {
    if (holdRafRef.current != null) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    holdStartRef.current = null;
    setHoldProgress(0);
  }, []);

  const holdLoop = useCallback(() => {
    const start = holdStartRef.current;
    if (start == null) return;
    const p = Math.min(1, (performance.now() - start) / BUDGET_NOTE_DELETE_HOLD_MS);
    setHoldProgress(p);
    if (p >= 1) {
      if (holdRafRef.current != null) {
        cancelAnimationFrame(holdRafRef.current);
        holdRafRef.current = null;
      }
      holdStartRef.current = null;
      const btn = btnRef.current;
      const pid = pointerIdRef.current;
      pointerIdRef.current = null;
      setHoldProgress(0);
      if (btn && pid != null) {
        try {
          btn.releasePointerCapture(pid);
        } catch {
          /* noop */
        }
      }
      onCompleteRef.current();
      return;
    }
    holdRafRef.current = requestAnimationFrame(holdLoop);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      cancelHold();
      holdStartRef.current = performance.now();
      pointerIdRef.current = e.pointerId;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      holdRafRef.current = requestAnimationFrame(holdLoop);
    },
    [cancelHold, holdLoop]
  );

  const onPointerEnd = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, treatAsShortClick: boolean) => {
      if (e.type === 'pointerleave' && e.currentTarget.hasPointerCapture(e.pointerId)) {
        return;
      }
      const start = holdStartRef.current;
      if (start == null) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* noop */
        }
      }
      pointerIdRef.current = null;
      const p = Math.min(1, (performance.now() - start) / BUDGET_NOTE_DELETE_HOLD_MS);
      if (p >= 1) {
        /* holdLoop가 이미 onComplete 호출 */
      } else {
        cancelHold();
        if (treatAsShortClick) onShortPressRef.current?.();
      }
      e.stopPropagation();
    },
    [cancelHold]
  );

  useEffect(() => {
    return () => {
      if (holdRafRef.current != null) cancelAnimationFrame(holdRafRef.current);
    };
  }, []);

  useEffect(() => {
    cancelHold();
  }, [actionKey, cancelHold]);

  return (
    <button
      ref={btnRef}
      type="button"
      className={`cp-budget-note-hold-action-btn${holdProgress > 0 ? ' is-holding' : ''}${className ? ` ${className}` : ''}`}
      onPointerDown={onPointerDown}
      onPointerUp={(e) => onPointerEnd(e, true)}
      onPointerCancel={(e) => onPointerEnd(e, false)}
      onPointerLeave={(e) => onPointerEnd(e, false)}
      onContextMenu={(e) => e.preventDefault()}
      title={title ?? '길게 눌러 실행'}
      aria-label={ariaLabel}
      {...rest}
    >
      <span className="cp-budget-note-hold-action-bar" style={{ transform: `scaleX(${holdProgress})` }} aria-hidden />
      <span className="cp-budget-note-hold-action-icon">{children}</span>
    </button>
  );
};

const BudgetNotePage: React.FC<{
  noteWorkspaceId: string | null;
  variant?: 'workspace' | 'account';
}> = ({ noteWorkspaceId, variant = 'workspace' }) => {
  const { setBudgetNoteDirty, saveBudgetNoteRef } = useContext(GradeTrackLeaveContext);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState('');
  const lastHydratedKeyRef = useRef('');
  const [rows, setRows] = useState<BudgetNoteRowDraft[]>([]);
  const [entries, setEntries] = useState<BudgetNoteUsageEntryDraft[]>([]);
  const [deletedRowIds, setDeletedRowIds] = useState<string[]>([]);
  const [deletedEntryIds, setDeletedEntryIds] = useState<string[]>([]);
  const baselineRef = useRef('');
  /** hydrate(기준 스냅샷) 완료 전에는 dirty로 보지 않음 — isLoading=false·baseline='' 조합 오탐 방지 */
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false);
  const savedRowIdsRef = useRef<Set<string>>(new Set());
  const savedEntryIdsRef = useRef<Set<string>>(new Set());
  const rowsRef = useRef(rows);
  const entriesRef = useRef(entries);
  const deletedRowIdsRef = useRef(deletedRowIds);
  const deletedEntryIdsRef = useRef(deletedEntryIds);
  rowsRef.current = rows;
  entriesRef.current = entries;
  deletedRowIdsRef.current = deletedRowIds;
  deletedEntryIdsRef.current = deletedEntryIds;

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? '');
    });
  }, []);

  const workspaceQuery = useBudgetNoteWorkspaceQuery({ userId, noteWorkspaceId, enabled: !!userId });
  const loading = !userId || workspaceQuery.isLoading || (!workspaceHydrated && !workspaceQuery.isError);

  useEffect(() => {
    lastHydratedKeyRef.current = '';
    baselineRef.current = '';
    setWorkspaceHydrated(false);
  }, [noteWorkspaceId]);

  useEffect(() => {
    if (workspaceQuery.error) {
      setLoadError(
        workspaceQuery.error instanceof Error
          ? workspaceQuery.error.message
          : '불러오기에 실패했습니다.'
      );
      return;
    }
    if (!workspaceQuery.isError) setLoadError(null);
  }, [workspaceQuery.error, workspaceQuery.isError]);

  useEffect(() => {
    if (!workspaceQuery.data || !userId) return;
    const hydrateKey = `${noteWorkspaceId ?? 'account'}:${workspaceQuery.dataUpdatedAt}`;
    if (lastHydratedKeyRef.current === hydrateKey) return;
    lastHydratedKeyRef.current = hydrateKey;

    const snap = workspaceQuery.data;
    const nextRows = snap.rows.map((r) => ({ ...r }));
    const nextEntries = snap.entries.map((e) => ({ ...e }));
    const nextLabels = normalizeBudgetNoteCategoryHeaderLabels(snap.categoryHeaderLabels);
    setRows(nextRows);
    setEntries(nextEntries);
    setCategoryHeaderLabels(nextLabels);
    setDeletedRowIds([]);
    setDeletedEntryIds([]);
    savedRowIdsRef.current = new Set(nextRows.map((r) => r.id));
    savedEntryIdsRef.current = new Set(nextEntries.map((e) => e.id));
    baselineRef.current = normalizeWorkspace(nextRows, nextEntries, [], [], nextLabels);
    setWorkspaceHydrated(true);
    clearBudgetNoteHistoryRef.current();
  }, [workspaceQuery.data, workspaceQuery.dataUpdatedAt, userId, noteWorkspaceId]);

  const [initialViewOptions] = useState(loadStoredBudgetViewOptions);
  /** false(기본): 추경예산·추경증감·추경잔액 열 숨김 */
  const [suppDataVisible, setSuppDataVisible] = useState(initialViewOptions.suppDataVisible);
  /** false(기본): 숨김 처리한 행 미표시. ON이면 연한 스타일로 표시 */
  const [hiddenDataVisible, setHiddenDataVisible] = useState(initialViewOptions.hiddenDataVisible);
  /** true: 현재잔액에 집행예정 금액까지 빼서 표시(해당 잔액 칸 형광 배경) */
  const [plannedInBalanceEnabled, setPlannedInBalanceEnabled] = useState(
    initialViewOptions.plannedInBalanceEnabled
  );
  /** 구분1~4·메모 열 표시 (토글, localStorage 저장) */
  const [metaColsVisible, setMetaColsVisible] = useState<Record<BudgetMetaColKey, boolean>>(loadStoredMetaColsVisible);
  /** 가로모드: 카테고리·메모 열 너비(px, localStorage 저장) */
  const [metaColWidthsPx, setMetaColWidthsPx] = useState<Record<BudgetMetaColKey, number>>(loadStoredMetaColWidthsPx);
  const [deltaColWidthPx, setDeltaColWidthPx] = useState<number>(loadStoredDeltaColWidthPx);
  /** 전역 카테고리 정렬 필터 (OFF=사용자 sort_order, ON=cat1~4 정렬) */
  const [categorySortFilterEnabled, setCategorySortFilterEnabled] = useState(
    initialViewOptions.categorySortFilterEnabled
  );
  const [categorySort, setCategorySort] = useState<Record<BudgetCategoryKey, BudgetCategorySortDir>>(
    initialViewOptions.categorySort
  );

  useEffect(() => {
    try {
      const payload: BudgetViewOptions = {
        suppDataVisible,
        hiddenDataVisible,
        plannedInBalanceEnabled,
        categorySortFilterEnabled,
        categorySort,
      };
      localStorage.setItem(BUDGET_VIEW_OPTIONS_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* noop */
    }
  }, [suppDataVisible, hiddenDataVisible, plannedInBalanceEnabled, categorySortFilterEnabled, categorySort]);
  const [categoryHeaderLabels, setCategoryHeaderLabels] = useState<BudgetNoteCategoryHeaderLabels>({
    ...DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS,
  });
  const categoryHeaderLabelsRef = useRef(categoryHeaderLabels);
  categoryHeaderLabelsRef.current = categoryHeaderLabels;
  const undoStackRef = useRef<BudgetNoteHistoryFrame[]>([]);
  const redoStackRef = useRef<BudgetNoteHistoryFrame[]>([]);
  const [historyRevision, setHistoryRevision] = useState(0);
  const isApplyingHistoryRef = useRef(false);
  const historyCoalescePendingRef = useRef(false);
  const historyCoalesceTimerRef = useRef<number | null>(null);
  const recordBudgetNoteEditRef = useRef<(mode?: 'coalesce' | 'discrete') => void>(() => undefined);
  const undoBudgetNoteEditRef = useRef<() => void>(() => undefined);
  const redoBudgetNoteEditRef = useRef<() => void>(() => undefined);
  const clearBudgetNoteHistoryRef = useRef<() => void>(() => undefined);
  const [editingCategoryKey, setEditingCategoryKey] = useState<null | BudgetCategoryKey>(null);
  const [categoryHeaderDraft, setCategoryHeaderDraft] = useState('');
  const categoryHeaderInputRef = useRef<HTMLInputElement>(null);
  const editingCategoryKeyRef = useRef<null | BudgetCategoryKey>(null);
  const categoryHeaderDraftRef = useRef('');
  const [layoutMode, setLayoutMode] = useState<'horizontal' | 'vertical'>(loadStoredBudgetLayoutMode);
  const vertCardsScrollRef = useRef<HTMLDivElement>(null);
  const [rowMenu, setRowMenu] = useState<BudgetRowMenuState | null>(null);
  /** 예산 구분·내용 열 보이기/숨기기 미니 모달 */
  const [metaColsMenu, setMetaColsMenu] = useState<null | { left: number; top: number }>(null);
  const [selectedMetaCells, setSelectedMetaCells] = useState<string[]>([]);
  const selectedMetaCellSet = useMemo(() => new Set(selectedMetaCells), [selectedMetaCells]);
  const hasMetaCellSelection = selectedMetaCells.length > 0;
  const [markColorPickModal, setMarkColorPickModal] = useState(false);
  const metaSelectionAnchorRef = useRef<null | { rowIdx: number; colKey: BudgetMetaColKey }>(null);
  const metaDragStateRef = useRef<null | {
    active: boolean;
    anchorRowIdx: number;
    anchorColKey: BudgetMetaColKey;
    currentRowIdx: number;
    currentColKey: BudgetMetaColKey;
  }>(null);
  /** 표시 순서: 필터 OFF=sort_order, 필터 ON=카테고리 정렬(표시만). 숨김 행은 토글 ON일 때만 */
  const displayRows = useMemo(() => {
    const sorted = sortRowsByCategorySort(rows, categorySortFilterEnabled, categorySort);
    if (hiddenDataVisible) return sorted;
    return sorted.filter((r) => !r.is_hidden);
  }, [rows, categorySortFilterEnabled, categorySort, hiddenDataVisible]);
  const displayRowsRef = useRef(displayRows);
  displayRowsRef.current = displayRows;

  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const searchMatches = useMemo(
    () =>
      collectBudgetNoteSearchMatches({
        query: searchQuery,
        rows: displayRows,
        entries,
        metaColsVisible,
      }),
    [searchQuery, displayRows, entries, metaColsVisible]
  );
  const activeMatch = searchMatches[activeMatchIndex] ?? null;
  const activeMatchId = activeMatch?.matchId ?? null;

  const runBudgetSearch = useCallback(() => {
    const q = normalizeBudgetSearchQuery(searchDraft);
    setSearchQuery(q);
    setActiveMatchIndex(0);
  }, [searchDraft]);

  const goPrevBudgetSearchMatch = useCallback(() => {
    if (!searchMatches.length) return;
    setActiveMatchIndex((i) => (i - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches.length]);

  const goNextBudgetSearchMatch = useCallback(() => {
    if (!searchMatches.length) return;
    setActiveMatchIndex((i) => (i + 1) % searchMatches.length);
  }, [searchMatches.length]);

  const clearBudgetSearch = useCallback(() => {
    setSearchDraft('');
    setSearchQuery('');
    setActiveMatchIndex(0);
  }, []);

  const handleSearchDraftChange = useCallback(
    (next: string) => {
      setSearchDraft(next);
      if (!next) {
        setSearchQuery('');
        setActiveMatchIndex(0);
      }
    },
    []
  );

  const hasSearchDraftText = searchDraft.length > 0;
  const showSearchNavSuffix = Boolean(searchQuery);

  useEffect(() => {
    if (activeMatchIndex >= searchMatches.length) {
      setActiveMatchIndex(0);
    }
  }, [activeMatchIndex, searchMatches.length]);

  useLayoutEffect(() => {
    if (!activeMatchId) return;
    const el = document.querySelector(`[data-budget-search-match="${CSS.escape(activeMatchId)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, [activeMatchId, searchQuery, layoutMode]);

  const selectedMetaCellsRef = useRef<string[]>([]);
  selectedMetaCellsRef.current = selectedMetaCells;

  const [rowDeleteConfirm, setRowDeleteConfirm] = useState<null | { rowId: string }>(null);
  const [usageModal, setUsageModal] = useState<null | { mode: 'add'; rowId: string } | { mode: 'edit'; entryId: string }>(
    null
  );
  const [usageForm, setUsageForm] = useState<{
    kind: BudgetUsageKind;
    usage_date: string;
    usage_due_kind: BudgetUsageDueKind;
    amount: string;
    memo: string;
  }>({ kind: 'planned', usage_date: toISODate(new Date()), usage_due_kind: 'day', amount: '', memo: '' });

  const usageAmountHangulLine = useMemo(
    () => budgetAmountDigitsToHangulWon(usageForm.amount),
    [usageForm.amount]
  );
  /** 예상 잔액 입력 중 원문(부호 포함). null 이면 금액 기준 계산값을 표시 */
  const [usageBalanceDraft, setUsageBalanceDraft] = useState<string | null>(null);

  /** 이 집행 전 잔액 = 사업예산 − (수정 중인 내역을 뺀) 집행완료·집행예정 블럭 합계 */
  const usageBalanceBase = useMemo(() => {
    if (!usageModal) return null;
    const editingId = usageModal.mode === 'edit' ? usageModal.entryId : null;
    const rowId =
      usageModal.mode === 'add' ? usageModal.rowId : entries.find((e) => e.id === editingId)?.row_id;
    if (!rowId) return null;
    const row = rows.find((r) => r.id === rowId);
    if (!row) return null;
    const usedOthers = entries
      .filter(
        (e) => e.row_id === rowId && (e.kind === 'completed' || e.kind === 'planned') && e.id !== editingId
      )
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return (Number(row.project_budget) || 0) - usedOthers;
  }, [usageModal, entries, rows]);

  const usageExpectedBalance =
    usageBalanceBase === null ? null : usageBalanceBase - (Number(usageForm.amount) || 0);

  const hiddenMetaColCount = useMemo(
    () => BUDGET_META_COL_KEYS.filter((k) => !metaColsVisible[k]).length,
    [metaColsVisible]
  );

  const usedCustomMarkColors = useMemo(() => collectUsedBudgetMarkCustomColors(rows), [rows]);

  const toolbarMarkColors = useMemo(
    () => [...DEFAULT_BUDGET_NOTE_MARK_COLORS, ...usedCustomMarkColors],
    [usedCustomMarkColors]
  );

  const budgetSuperHeaderColSpans = useMemo(() => {
    const metaColSpan = BUDGET_META_COL_KEYS.filter((k) => metaColsVisible[k]).length;
    const planColSpan = suppDataVisible ? 3 : 1;
    const usageColSpan = suppDataVisible ? 3 : 2;
    return { metaColSpan, planColSpan, usageColSpan };
  }, [metaColsVisible, suppDataVisible]);

  const metaColsPlaceholderVisible = budgetSuperHeaderColSpans.metaColSpan === 0;

  const budgetTableEmptyColSpan =
    (suppDataVisible ? 14 : 11) - hiddenMetaColCount + (metaColsPlaceholderVisible ? 1 : 0);

  const beginMetaColResize = useCallback(
    (key: BudgetMetaColKey) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!metaColsVisible[key]) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      const startX = e.clientX;
      const startW = metaColWidthsPx[key];

      const onMove = (ev: PointerEvent) => {
        ev.preventDefault();
        const next = Math.min(
          BUDGET_H_META_COL_RESIZE_MAX_PX,
          Math.max(BUDGET_H_META_COL_RESIZE_MIN_PX, Math.round(startW + ev.clientX - startX))
        );
        setMetaColWidthsPx((prev) => (prev[key] === next ? prev : { ...prev, [key]: next }));
      };
      const onUp = (ev: PointerEvent) => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        try {
          el.releasePointerCapture(ev.pointerId);
        } catch {
          /* noop */
        }
      };
      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
    },
    [metaColsVisible, metaColWidthsPx]
  );

  const beginDeltaColResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      const startX = e.clientX;
      const startW = deltaColWidthPx;
      const onMove = (ev: PointerEvent) => {
        ev.preventDefault();
        const next = clampDeltaColWidthPx(startW + ev.clientX - startX) ?? startW;
        setDeltaColWidthPx((prev) => (prev === next ? prev : next));
      };
      const onUp = (ev: PointerEvent) => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        try {
          el.releasePointerCapture(ev.pointerId);
        } catch {
          /* noop */
        }
      };
      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
    },
    [deltaColWidthPx]
  );

  useEffect(() => {
    try {
      localStorage.setItem(BUDGET_H_DELTA_COL_WIDTH_STORAGE_KEY, String(deltaColWidthPx));
    } catch {
      /* noop */
    }
  }, [deltaColWidthPx]);

  const horizontalColgroup = useMemo(() => {
    const m = BUDGET_H_METRIC_COL_PX;

    return (
      <colgroup>
        <col style={{ width: BUDGET_H_ROWACT_COL_PX }} />
        {metaColsPlaceholderVisible ? <col key="meta-placeholder" style={{ width: 148 }} /> : null}
        {BUDGET_CATEGORY_KEYS.map((ck) =>
          metaColsVisible[ck] ? <col key={ck} style={{ width: metaColWidthsPx[ck] }} /> : null
        )}
        {metaColsVisible.memo ? <col key="memo" style={{ width: metaColWidthsPx.memo }} /> : null}
        <col style={{ width: m }} />
        {suppDataVisible ? (
          <>
            <col style={{ width: m }} />
            <col style={{ width: deltaColWidthPx }} />
          </>
        ) : null}
        <col style={{ width: m }} />
        <col style={{ width: m }} />
        <col style={{ width: m }} />
        {suppDataVisible ? <col style={{ width: m }} /> : null}
        {/* 너비 미지정: 남는 가로를 집행 열이 흡수 (최소 너비는 테이블 minWidth로 보장) */}
        <col />
      </colgroup>
    );
  }, [metaColWidthsPx, metaColsVisible, metaColsPlaceholderVisible, suppDataVisible, deltaColWidthPx]);

  const horizontalTableMinWidthPx = useMemo(() => {
    let w = BUDGET_H_ROWACT_COL_PX;
    if (metaColsPlaceholderVisible) w += 148;
    for (const ck of BUDGET_CATEGORY_KEYS) {
      if (metaColsVisible[ck]) w += metaColWidthsPx[ck];
    }
    if (metaColsVisible.memo) w += metaColWidthsPx.memo;
    w += (suppDataVisible ? 6 : 4) * BUDGET_H_METRIC_COL_PX;
    if (suppDataVisible) w += deltaColWidthPx;
    w += BUDGET_H_USAGE_COL_MIN_PX;
    return w;
  }, [metaColWidthsPx, metaColsVisible, metaColsPlaceholderVisible, suppDataVisible, deltaColWidthPx]);

  const dirty = useMemo(() => {
    if (!workspaceHydrated || !baselineRef.current) return false;
    return (
      normalizeWorkspace(rows, entries, deletedRowIds, deletedEntryIds, categoryHeaderLabels) !==
      baselineRef.current
    );
  }, [workspaceHydrated, rows, entries, deletedRowIds, deletedEntryIds, categoryHeaderLabels]);

  useEffect(() => {
    setBudgetNoteDirty(dirty);
    return () => setBudgetNoteDirty(false);
  }, [dirty, setBudgetNoteDirty]);

  useEffect(() => {
    if (!dirty || loading) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty, loading]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const rowsToSave = categorySortFilterEnabled
        ? applyActiveCategorySortToRows(rows, categorySortFilterEnabled, categorySort)
        : [...rows].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
      await saveBudgetNoteWorkspace({
        noteWorkspaceId,
        rows: rowsToSave,
        entries,
        deletedRowIds,
        deletedEntryIds,
        categoryHeaderLabels,
      });
      const keptRowIds = new Set(rowsToSave.filter((r) => !deletedRowIds.includes(r.id)).map((r) => r.id));
      const keptEntryIds = new Set(entries.filter((e) => !deletedEntryIds.includes(e.id)).map((e) => e.id));
      const savedRows = rowsToSave.filter((r) => keptRowIds.has(r.id));
      const savedEntries = entries.filter((e) => keptEntryIds.has(e.id));
      setRows(savedRows);
      setEntries(savedEntries);
      setDeletedRowIds([]);
      setDeletedEntryIds([]);
      savedRowIdsRef.current = new Set(savedRows.map((r) => r.id));
      savedEntryIdsRef.current = new Set(savedEntries.map((e) => e.id));
      const savedLabels = normalizeBudgetNoteCategoryHeaderLabels(categoryHeaderLabels);
      setCategoryHeaderLabels(savedLabels);
      baselineRef.current = normalizeWorkspace(savedRows, savedEntries, [], [], savedLabels);
      setWorkspaceHydrated(true);
      clearBudgetNoteHistoryRef.current();
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [rows, categorySortFilterEnabled, categorySort, entries, deletedRowIds, deletedEntryIds, noteWorkspaceId, categoryHeaderLabels]);

  useEffect(() => {
    saveBudgetNoteRef.current = { save: handleSave };
    return () => {
      saveBudgetNoteRef.current = null;
    };
  }, [handleSave, saveBudgetNoteRef]);

  const patchRow = (id: string, patch: Partial<BudgetNoteRowDraft>) => {
    recordBudgetNoteEditRef.current('coalesce');
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const selectableMetaCols = useMemo(
    () => getBudgetSelectableMetaCols(metaColsVisible),
    [metaColsVisible]
  );

  const handleMetaCellMouseDown = useCallback(
    (rowIdx: number, colKey: BudgetMetaColKey, shiftKey: boolean) => {
      if (layoutMode !== 'horizontal') return;
      if (!metaColsVisible[colKey]) return;
      const ordered = displayRowsRef.current;
      const row = ordered[rowIdx];
      if (!row) return;

      if (shiftKey && metaSelectionAnchorRef.current) {
        const keys = rectBudgetMetaCellKeys(
          ordered,
          selectableMetaCols,
          metaSelectionAnchorRef.current,
          { rowIdx, colKey }
        );
        metaDragStateRef.current = null;
        setSelectedMetaCells(keys);
        return;
      }

      metaSelectionAnchorRef.current = { rowIdx, colKey };
      metaDragStateRef.current = {
        active: true,
        anchorRowIdx: rowIdx,
        anchorColKey: colKey,
        currentRowIdx: rowIdx,
        currentColKey: colKey,
      };
      setSelectedMetaCells([budgetMetaCellKey(row.id, colKey)]);
    },
    [layoutMode, metaColsVisible, selectableMetaCols]
  );

  const handleMetaCellMouseEnter = useCallback(
    (rowIdx: number, colKey: BudgetMetaColKey) => {
      if (layoutMode !== 'horizontal') return;
      const state = metaDragStateRef.current;
      if (!state?.active) return;
      const keys = rectBudgetMetaCellKeys(
        displayRowsRef.current,
        selectableMetaCols,
        { rowIdx: state.anchorRowIdx, colKey: state.anchorColKey },
        { rowIdx, colKey }
      );
      metaDragStateRef.current = { ...state, currentRowIdx: rowIdx, currentColKey: colKey };
      setSelectedMetaCells(keys);
    },
    [layoutMode, selectableMetaCols]
  );

  const applyMarkColorToSelected = useCallback((hex: string) => {
    const currentSel = selectedMetaCellsRef.current;
    if (!currentSel.length) return;
    const normalized = hex ? normalizeBudgetMarkHex(hex) : '';
    const patchById = new Map<string, Partial<BudgetNoteRowDraft>>();
    for (const cellKey of currentSel) {
      const parsed = parseBudgetMetaCellKey(cellKey);
      if (!parsed) continue;
      const colorField = BUDGET_META_COLOR_FIELD[parsed.colKey];
      const prevPatch = patchById.get(parsed.rowId) ?? {};
      patchById.set(parsed.rowId, { ...prevPatch, [colorField]: normalized });
    }
    if (patchById.size === 0) return;
    recordBudgetNoteEditRef.current('discrete');
    setRows((prev) =>
      prev.map((r) => (patchById.has(r.id) ? { ...r, ...patchById.get(r.id)! } : r))
    );
    setSelectedMetaCells([]);
  }, []);

  const handleMarkColorModalSubmit = useCallback(
    (payload: ProgramColorModalSubmitPayload) => {
      const hex = normalizeBudgetMarkHex(payload.hex);
      setMarkColorPickModal(false);
      applyMarkColorToSelected(hex);
    },
    [applyMarkColorToSelected]
  );

  const renderMarkColorSwatchBtn = useCallback(
    (hex: string) => {
      const themeInk = isBudgetMarkThemeInk(hex);
      return (
        <button
          key={hex}
          type="button"
          className={
            'cp-dedicated-color-btn' + (themeInk ? ' cp-budget-note-mark-swatch--theme-ink' : '')
          }
          onClick={() => applyMarkColorToSelected(hex)}
          disabled={!hasMetaCellSelection}
          title={budgetMarkColorAriaLabel(hex)}
          aria-label={budgetMarkColorAriaLabel(hex)}
        >
          <span
            className="cp-dedicated-color-btn-inner"
            style={themeInk ? undefined : { backgroundColor: hex }}
            aria-hidden
          />
        </button>
      );
    },
    [applyMarkColorToSelected, hasMetaCellSelection]
  );

  useEffect(() => {
    const onMouseUp = () => {
      if (metaDragStateRef.current?.active) {
        metaDragStateRef.current = { ...metaDragStateRef.current, active: false };
      }
    };
    document.addEventListener('mouseup', onMouseUp, true);
    return () => document.removeEventListener('mouseup', onMouseUp, true);
  }, []);

  const renderHorizontalMetaCell = useCallback(
    (
      row: BudgetNoteRowDraft,
      rowIndex: number,
      colKey: BudgetMetaColKey,
      tdClass: string,
      ariaLabel: string,
      textValue: string,
      colorValue: string,
      onTextChange: (v: string) => void,
      onPaste: React.ClipboardEventHandler<HTMLInputElement>
    ) => {
      const cellKey = budgetMetaCellKey(row.id, colKey);
      const isSelected = selectedMetaCellSet.has(cellKey);
      const markHex = (colorValue ?? '').trim();
      const isMemoCol = colKey === 'memo';
      const memoFill = isMemoCol && markHex ? budgetMarkMemoFillStyle(markHex) : null;
      const memoThemeInk = isMemoCol && markHex ? isBudgetMarkThemeInk(markHex) : false;
      const selectedSingle = isSelected && selectedMetaCellSet.size === 1;
      const selectedMulti = isSelected && selectedMetaCellSet.size > 1;
      let edgeTop = false;
      let edgeBottom = false;
      let edgeLeft = false;
      let edgeRight = false;
      if (selectedMulti) {
        const neighbors = budgetMetaCellNeighborKeys(
          displayRows,
          selectableMetaCols,
          rowIndex,
          colKey
        );
        edgeTop = !neighbors.top || !selectedMetaCellSet.has(neighbors.top);
        edgeBottom = !neighbors.bottom || !selectedMetaCellSet.has(neighbors.bottom);
        edgeLeft = !neighbors.left || !selectedMetaCellSet.has(neighbors.left);
        edgeRight = !neighbors.right || !selectedMetaCellSet.has(neighbors.right);
      }
      return (
        <td
          key={colKey}
          className={[
            `cp-budget-note-td ${tdClass}`,
            selectedSingle ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-single' : '',
            selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
            edgeTop ? 'cp-base-timetable-cell-edge-top' : '',
            edgeBottom ? 'cp-base-timetable-cell-edge-bottom' : '',
            edgeLeft ? 'cp-base-timetable-cell-edge-left' : '',
            edgeRight ? 'cp-base-timetable-cell-edge-right' : '',
            memoFill || memoThemeInk ? 'cp-budget-note-td--memo-mark-fill' : '',
            memoThemeInk ? 'cp-budget-note-td--memo-mark-fill-theme-ink' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={
            memoFill
              ? {
                  ['--cp-budget-memo-mark-bg' as string]: memoFill.backgroundColor,
                  ['--cp-budget-memo-mark-fg' as string]: memoFill.color,
                }
              : undefined
          }
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            const alreadyOnlySelected =
              selectedMetaCellSet.size === 1 && selectedMetaCellSet.has(cellKey);
            if (alreadyOnlySelected && !metaDragStateRef.current?.active) return;
            e.preventDefault();
            handleMetaCellMouseDown(rowIndex, colKey, e.shiftKey);
          }}
          onMouseEnter={() => handleMetaCellMouseEnter(rowIndex, colKey)}
        >
          {markHex && !isMemoCol ? (
            <span
              className={
                'cp-budget-note-cell-mark' +
                (isBudgetMarkThemeInk(markHex) ? ' cp-budget-note-cell-mark--theme-ink' : '')
              }
              style={isBudgetMarkThemeInk(markHex) ? undefined : { backgroundColor: markHex }}
              aria-hidden
            />
          ) : null}
          <div className="cp-budget-note-meta-cell-inner">
            <BudgetNoteSearchMirroredInput
              className="cp-budget-note-input cp-budget-note-input--single"
              value={textValue}
              query={searchQuery}
              activeMatchId={activeMatchId}
              buildMatchId={(start) => buildBudgetMetaSearchMatchId(row.id, colKey, start)}
              onChange={onTextChange}
              onPaste={onPaste}
              ariaLabel={ariaLabel}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          </div>
        </td>
      );
    },
    [handleMetaCellMouseDown, handleMetaCellMouseEnter, displayRows, selectableMetaCols, selectedMetaCellSet, searchQuery, activeMatchId]
  );

  const renderVerticalMetaField = useCallback(
    (
      rowId: string,
      colKey: BudgetMetaColKey,
      label: string,
      ariaLabel: string,
      textValue: string,
      colorValue: string,
      onTextChange: (v: string) => void,
      onPaste: React.ClipboardEventHandler<HTMLInputElement>
    ) => {
      const markHex = (colorValue ?? '').trim();
      const hasMark = Boolean(markHex);
      const isMemoCol = colKey === 'memo';
      const memoFill = isMemoCol && markHex ? budgetMarkMemoFillStyle(markHex) : null;
      const memoThemeInk = isMemoCol && markHex ? isBudgetMarkThemeInk(markHex) : false;
      return (
        <div
          className={[
            'cp-budget-note-vert-field cp-budget-note-vert-field--plain cp-budget-note-vert-field--meta',
            isMemoCol ? 'cp-budget-note-vert-field--memo' : '',
            memoFill || memoThemeInk ? 'cp-budget-note-vert-field--memo-mark-fill' : '',
            memoThemeInk ? 'cp-budget-note-vert-field--memo-mark-fill-theme-ink' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={
            memoFill
              ? {
                  ['--cp-budget-memo-mark-bg' as string]: memoFill.backgroundColor,
                  ['--cp-budget-memo-mark-fg' as string]: memoFill.color,
                }
              : undefined
          }
        >
          {hasMark && !isMemoCol ? (
            <span
              className={
                'cp-budget-note-vert-cell-mark' +
                (isBudgetMarkThemeInk(markHex) ? ' cp-budget-note-vert-cell-mark--theme-ink' : '')
              }
              style={
                !isBudgetMarkThemeInk(markHex) ? { backgroundColor: markHex } : undefined
              }
              aria-hidden
            />
          ) : isMemoCol ? null : (
            <span className="cp-budget-note-vert-cell-mark cp-budget-note-vert-cell-mark--empty" aria-hidden />
          )}
          <div className="cp-budget-note-vert-field-body">
            <span className="cp-budget-note-vert-label">{label}</span>
            <BudgetNoteSearchMirroredInput
              className="cp-budget-note-input cp-budget-note-input--single cp-budget-note-vert-input"
              value={textValue}
              query={searchQuery}
              activeMatchId={activeMatchId}
              buildMatchId={(start) => buildBudgetMetaSearchMatchId(rowId, colKey, start)}
              onChange={onTextChange}
              onPaste={onPaste}
              ariaLabel={ariaLabel}
            />
          </div>
        </div>
      );
    },
    [searchQuery, activeMatchId]
  );

  const handleBudgetInputPaste = useCallback(
    (rowIndex: number, colKey: BudgetNotePasteColKey) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text/plain');
      if (!shouldHandleBudgetBulkPaste(text)) return;
      e.preventDefault();
      const grid = parseBudgetNotePasteGrid(text);
      if (!grid.length || !userId) return;
      const pasteableCols = getBudgetPasteableColumnKeys(metaColsVisible, suppDataVisible);
      const startColIndex = pasteableCols.indexOf(colKey);
      if (startColIndex < 0) return;

      recordBudgetNoteEditRef.current('discrete');
      setRows((prev) => {
        const ordered = [...prev].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
        let maxSort = ordered.reduce((m, r) => Math.max(m, r.sort_order), -1);
        const next = [...prev];
        const patchById = new Map<string, Partial<BudgetNoteRowDraft>>();

        for (let ri = 0; ri < grid.length; ri += 1) {
          const targetIdx = rowIndex + ri;
          let row = ordered[targetIdx];
          if (!row) {
            row = emptyRow(userId, ++maxSort, noteWorkspaceId);
            ordered.push(row);
            next.push(row);
          }
          let patch = patchById.get(row.id) ?? {};
          for (let ci = 0; ci < grid[ri].length; ci += 1) {
            const colIdx = startColIndex + ci;
            if (colIdx >= pasteableCols.length) break;
            patch = {
              ...patch,
              ...budgetPasteCellToPatch(pasteableCols[colIdx], grid[ri][ci] ?? ''),
            };
          }
          patchById.set(row.id, patch);
        }

        if (patchById.size === 0) return prev;
        return next.map((r) => (patchById.has(r.id) ? { ...r, ...patchById.get(r.id)! } : r));
      });
    },
    [userId, noteWorkspaceId, metaColsVisible, suppDataVisible]
  );

  const addRow = () => {
    if (!userId) return;
    const maxSort = rows.reduce((m, r) => Math.max(m, r.sort_order), -1);
    const nr = emptyRow(userId, maxSort + 1, noteWorkspaceId);
    recordBudgetNoteEditRef.current('discrete');
    setRows((prev) => [...prev, nr]);
  };

  const executeDeleteRow = useCallback((rowId: string) => {
    setRowMenu(null);
    setRowDeleteConfirm(null);
    setUsageHover(null);
    recordBudgetNoteEditRef.current('discrete');
    const toMark = entriesRef.current.filter((e) => e.row_id === rowId).map((e) => e.id);
    if (toMark.length) {
      setDeletedEntryIds((d) => {
        const s = new Set(d);
        toMark.forEach((id) => {
          if (savedEntryIdsRef.current.has(id)) s.add(id);
        });
        return Array.from(s);
      });
    }
    setEntries((prev) => prev.filter((e) => e.row_id !== rowId));
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    if (savedRowIdsRef.current.has(rowId)) {
      setDeletedRowIds((d) => (d.includes(rowId) ? d : [...d, rowId]));
    }
  }, []);

  const requestDeleteRow = useCallback((rowId: string) => {
    setRowMenu(null);
    setRowDeleteConfirm({ rowId });
  }, []);

  const duplicateBudgetRow = useCallback(
    (rowId: string) => {
      if (!userId) return;
      setRowMenu(null);
      const src = rowsRef.current.find((r) => r.id === rowId);
      if (!src) return;
      const newRowId = generateId();
      const srcSo = src.sort_order;
      const ws = src.workspace_id ?? noteWorkspaceId;
      recordBudgetNoteEditRef.current('discrete');
      setRows((prev) => {
        const bumped = prev.map((r) => (r.sort_order > srcSo ? { ...r, sort_order: r.sort_order + 1 } : r));
        const base = bumped.find((r) => r.id === rowId);
        if (!base) return prev;
        const copy: BudgetNoteRowDraft = {
          ...base,
          id: newRowId,
          sort_order: srcSo + 1,
          workspace_id: ws,
          is_hidden: false,
        };
        return [...bumped, copy];
      });
      setEntries((prev) => {
        const clones = prev
          .filter((e) => e.row_id === rowId)
          .map((e) => ({
            ...e,
            id: generateId(),
            row_id: newRowId,
            user_id: userId,
            workspace_id: ws,
          }));
        if (clones.length === 0) return prev;
        return [...prev, ...clones];
      });
    },
    [userId, noteWorkspaceId]
  );

  const toggleBudgetRowHidden = useCallback((rowId: string) => {
    setRowMenu(null);
    const src = rowsRef.current.find((r) => r.id === rowId);
    if (!src) return;
    recordBudgetNoteEditRef.current('discrete');
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, is_hidden: !r.is_hidden } : r)));
  }, []);

  const moveSelectedRows = useCallback((direction: -1 | 1) => {
    const selectedRowIds = new Set<string>();
    for (const cellKey of selectedMetaCellsRef.current) {
      const parsed = parseBudgetMetaCellKey(cellKey);
      if (parsed) selectedRowIds.add(parsed.rowId);
    }
    if (!selectedRowIds.size) return;

    recordBudgetNoteEditRef.current('discrete');
    setRows((prev) => {
      const ordered = [...prev].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
      const selectedIndices = ordered
        .map((r, i) => (selectedRowIds.has(r.id) ? i : -1))
        .filter((i) => i >= 0)
        .sort((a, b) => a - b);
      if (!selectedIndices.length) return prev;

      const minIdx = selectedIndices[0];
      const maxIdx = selectedIndices[selectedIndices.length - 1];

      let next: BudgetNoteRowDraft[];
      if (direction < 0) {
        if (minIdx <= 0) return prev;
        const pivot = ordered[minIdx - 1];
        const before = ordered.slice(0, minIdx - 1);
        const block = ordered.slice(minIdx, maxIdx + 1);
        const after = ordered.slice(maxIdx + 1);
        next = [...before, ...block, pivot, ...after];
      } else {
        if (maxIdx >= ordered.length - 1) return prev;
        const pivot = ordered[maxIdx + 1];
        const before = ordered.slice(0, minIdx);
        const block = ordered.slice(minIdx, maxIdx + 1);
        const after = ordered.slice(maxIdx + 2);
        next = [...before, pivot, ...block, ...after];
      }
      return next.map((row, i) => ({ ...row, sort_order: i }));
    });
  }, []);

  const toggleCategorySort = (key: BudgetCategoryKey) => {
    setCategorySort((s) => ({ ...s, [key]: s[key] === 'asc' ? 'desc' : 'asc' }));
  };

  useEffect(() => {
    editingCategoryKeyRef.current = editingCategoryKey;
  }, [editingCategoryKey]);
  useEffect(() => {
    categoryHeaderDraftRef.current = categoryHeaderDraft;
  }, [categoryHeaderDraft]);

  useLayoutEffect(() => {
    if (!editingCategoryKey) return;
    const el = categoryHeaderInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editingCategoryKey]);

  useEffect(() => {
    try {
      localStorage.setItem(BUDGET_LAYOUT_MODE_STORAGE_KEY, layoutMode);
    } catch {
      /* noop */
    }
  }, [layoutMode]);

  /**
   * 세로모드: 카드(overflow-y) 위에서도 컨테이너 가로 스크롤이 되게 함.
   * - 트랙패드·Shift+휠: 가로 제스처를 카드에 뺏기지 않도록 capture
   * - 포인터 드래그: 가로가 우세하면 컨테이너 scrollLeft 이동
   */
  useEffect(() => {
    if (layoutMode !== 'vertical') return;
    const el = vertCardsScrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;

      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      const shiftAsHorizontal = e.shiftKey && absY > 0 && absX <= absY;
      if (!shiftAsHorizontal && absX < absY) return;

      const dx = shiftAsHorizontal ? e.deltaY : e.deltaX;
      if (dx === 0) return;

      const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + dx));
      if (next === el.scrollLeft) return;
      e.preventDefault();
      el.scrollLeft = next;
    };

    let dragging = false;
    let claimed = false;
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let pointerId: number | null = null;

    const isInteractiveTarget = (t: EventTarget | null) =>
      t instanceof Element &&
      Boolean(
        t.closest(
          'input, textarea, select, button, a, label, [role="button"], [contenteditable="true"]'
        )
      );

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (isInteractiveTarget(e.target)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      dragging = true;
      claimed = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startScroll = el.scrollLeft;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || pointerId !== e.pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!claimed) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dx) <= Math.abs(dy)) {
          dragging = false;
          pointerId = null;
          return;
        }
        claimed = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* noop */
        }
      }
      e.preventDefault();
      el.scrollLeft = startScroll - dx;
    };

    const endPointer = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      dragging = false;
      claimed = false;
      pointerId = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endPointer);
    el.addEventListener('pointercancel', endPointer);
    return () => {
      el.removeEventListener('wheel', onWheel, { capture: true });
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endPointer);
      el.removeEventListener('pointercancel', endPointer);
    };
  }, [layoutMode, displayRows.length]);

  useEffect(() => {
    try {
      localStorage.setItem(BUDGET_META_COLS_VISIBLE_STORAGE_KEY, JSON.stringify(metaColsVisible));
    } catch {
      /* noop */
    }
  }, [metaColsVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(BUDGET_H_META_COL_WIDTHS_STORAGE_KEY, JSON.stringify(metaColWidthsPx));
    } catch {
      /* noop */
    }
  }, [metaColWidthsPx]);

  const commitCategoryHeaderEdit = useCallback(() => {
    const key = editingCategoryKeyRef.current;
    if (!key) return;
    const t = categoryHeaderDraftRef.current.trim();
    const nextLabel = t === '' ? DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS[key] : t;
    if (nextLabel === (categoryHeaderLabelsRef.current[key] ?? DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS[key])) {
      setEditingCategoryKey(null);
      setCategoryHeaderDraft('');
      return;
    }
    recordBudgetNoteEditRef.current('discrete');
    setCategoryHeaderLabels((prev) => ({ ...prev, [key]: nextLabel }));
    setEditingCategoryKey(null);
    setCategoryHeaderDraft('');
  }, []);

  const cancelCategoryHeaderEdit = useCallback(() => {
    setEditingCategoryKey(null);
    setCategoryHeaderDraft('');
  }, []);

  const beginEditCategoryHeader = useCallback(
    (ck: BudgetCategoryKey) => {
      if (editingCategoryKeyRef.current === ck) {
        queueMicrotask(() => {
          categoryHeaderInputRef.current?.focus();
          categoryHeaderInputRef.current?.select();
        });
        return;
      }
      if (editingCategoryKeyRef.current && editingCategoryKeyRef.current !== ck) {
        commitCategoryHeaderEdit();
      }
      setEditingCategoryKey(ck);
      setCategoryHeaderDraft(categoryHeaderLabels[ck] ?? DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS[ck]);
    },
    [categoryHeaderLabels, commitCategoryHeaderEdit]
  );

  const openUsageAddModal = (rowId: string) => {
    setUsageHover(null);
    setUsageModal({ mode: 'add', rowId });
    setUsageBalanceDraft(null);
    setUsageForm({
      kind: 'planned',
      usage_date: toISODate(new Date()),
      usage_due_kind: 'day',
      amount: '',
      memo: '',
    });
  };

  const openUsageEditModal = (entryId: string) => {
    setUsageHover(null);
    const en = entries.find((e) => e.id === entryId);
    if (!en) return;
    setUsageModal({ mode: 'edit', entryId });
    setUsageBalanceDraft(null);
    setUsageForm({
      kind: en.kind,
      usage_date: en.usage_date,
      usage_due_kind: 'day',
      amount: String(Math.max(0, Math.round(Number(en.amount) || 0))),
      memo: en.memo,
    });
  };

  const closeUsageModal = () => {
    setUsageModal(null);
    setUsageHover(null);
  };

  const submitUsageModal = () => {
    if (!usageModal || !userId) return;
    const amount = parseMoneyInput(usageForm.amount);
    recordBudgetNoteEditRef.current('discrete');
    if (usageModal.mode === 'add') {
      const rowId = usageModal.rowId;
      const rowEntries = entries.filter((e) => e.row_id === rowId);
      const maxSort = rowEntries.reduce((m, e) => Math.max(m, e.sort_order), -1);
      const next: BudgetNoteUsageEntryDraft = {
        id: generateId(),
        user_id: userId,
        workspace_id: noteWorkspaceId,
        row_id: rowId,
        kind: usageForm.kind,
        usage_date: usageForm.usage_date.trim() || toISODate(new Date()),
        amount,
        memo: usageForm.memo.trim(),
        sort_order: maxSort + 1,
      };
      setEntries((prev) => [...prev, next]);
    } else {
      const entryId = usageModal.entryId;
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId
            ? {
                ...e,
                kind: usageForm.kind,
                usage_date: usageForm.usage_date.trim() || toISODate(new Date()),
                amount,
                memo: usageForm.memo.trim(),
              }
            : e
        )
      );
    }
    closeUsageModal();
  };

  const removeUsageEntry = (entryId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    recordBudgetNoteEditRef.current('discrete');
    setEntries((prev) => prev.filter((x) => x.id !== entryId));
    setUsageHover((h) => (h?.entry.id === entryId ? null : h));
    if (savedEntryIdsRef.current.has(entryId)) {
      setDeletedEntryIds((d) => (d.includes(entryId) ? d : [...d, entryId]));
    }
  };

  const entriesForRow = (rowId: string) =>
    entries
      .filter((e) => e.row_id === rowId)
      .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));

  const usedCompletedSum = (rowId: string) =>
    entries
      .filter((e) => e.row_id === rowId && e.kind === 'completed')
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const plannedSum = (rowId: string) =>
    entries
      .filter((e) => e.row_id === rowId && e.kind === 'planned')
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);

  /** 금액 열 드래그 선택 → 합계 미니 팝업 (가로 표 전용, 표시 순서 기준 사각형) */
  const numCols = useMemo<BudgetNumColKey[]>(
    () =>
      suppDataVisible
        ? ['cur', 'supp', 'delta', 'proj', 'used', 'bal', 'suppbal']
        : ['cur', 'proj', 'used', 'bal'],
    [suppDataVisible]
  );
  const [numSel, setNumSel] = useState<null | { anchor: BudgetNumCellPos; focus: BudgetNumCellPos }>(null);
  const numDragActiveRef = useRef(false);
  const [numSumPopPos, setNumSumPopPos] = useState<CSSProperties | null>(null);

  const numSelRect = useMemo(() => {
    if (!numSel) return null;
    const a = numCols.indexOf(numSel.anchor.col);
    const f = numCols.indexOf(numSel.focus.col);
    if (a < 0 || f < 0) return null;
    return {
      r0: Math.min(numSel.anchor.row, numSel.focus.row),
      r1: Math.max(numSel.anchor.row, numSel.focus.row),
      c0: Math.min(a, f),
      c1: Math.max(a, f),
    };
  }, [numSel, numCols]);
  const numSelIsMulti = !!numSelRect && (numSelRect.r1 > numSelRect.r0 || numSelRect.c1 > numSelRect.c0);

  const numSelSummary = useMemo(() => {
    if (!numSelRect || !numSelIsMulti) return null;
    const usedByRow = new Map<string, number>();
    const plannedByRow = new Map<string, number>();
    for (const e of entries) {
      const target = e.kind === 'completed' ? usedByRow : e.kind === 'planned' ? plannedByRow : null;
      if (!target) continue;
      target.set(e.row_id, (target.get(e.row_id) ?? 0) + (Number(e.amount) || 0));
    }
    let sum = 0;
    let count = 0;
    for (let r = numSelRect.r0; r <= numSelRect.r1; r++) {
      const row = displayRows[r];
      if (!row) continue;
      const cur = Number(row.current_budget) || 0;
      const supp = row.supp_budget === null || row.supp_budget === undefined ? null : Number(row.supp_budget) || 0;
      const proj = Number(row.project_budget) || 0;
      const used = usedByRow.get(row.id) ?? 0;
      const plannedInBalance = plannedInBalanceEnabled ? plannedByRow.get(row.id) ?? 0 : 0;
      for (let c = numSelRect.c0; c <= numSelRect.c1; c++) {
        const v = budgetNumCellValue(numCols[c], { cur, supp, proj, used, plannedInBalance });
        if (v === null) continue;
        sum += v;
        count += 1;
      }
    }
    const cells = (numSelRect.r1 - numSelRect.r0 + 1) * (numSelRect.c1 - numSelRect.c0 + 1);
    return { sum, count, cells };
  }, [numSelRect, numSelIsMulti, displayRows, numCols, entries, plannedInBalanceEnabled]);

  const handleNumCellMouseDown = (row: number, col: BudgetNumColKey, e: React.MouseEvent) => {
    if (e.button !== 0 || layoutMode !== 'horizontal') return;
    setSelectedMetaCells([]);
    if (e.shiftKey && numSel) {
      e.preventDefault();
      setNumSel({ anchor: numSel.anchor, focus: { row, col } });
      return;
    }
    numDragActiveRef.current = true;
    setNumSel({ anchor: { row, col }, focus: { row, col } });
  };

  const handleNumCellMouseEnter = (row: number, col: BudgetNumColKey) => {
    if (!numDragActiveRef.current) return;
    setNumSel((prev) => {
      if (!prev || (prev.focus.row === row && prev.focus.col === col)) return prev;
      return { anchor: prev.anchor, focus: { row, col } };
    });
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('[data-bn-num]')) active.blur();
    window.getSelection()?.removeAllRanges();
    document.body.classList.add('cp-bn-num-dragging');
  };

  /** 사업예산 헤더 버튼 대상: 선택 범위에 사업예산 열이 포함된 행 */
  const projFromCurTargetRowIds = useMemo(() => {
    if (!numSelRect) return [];
    const projIdx = numCols.indexOf('proj');
    if (projIdx < numSelRect.c0 || projIdx > numSelRect.c1) return [];
    const ids: string[] = [];
    for (let r = numSelRect.r0; r <= numSelRect.r1; r++) {
      const row = displayRows[r];
      if (row) ids.push(row.id);
    }
    return ids;
  }, [numSelRect, numCols, displayRows]);
  const [projFromCurConfirm, setProjFromCurConfirm] = useState<null | { rowIds: string[] }>(null);

  const applyProjFromCur = (rowIds: string[]) => {
    const idSet = new Set(rowIds);
    recordBudgetNoteEditRef.current('discrete');
    setRows((prev) =>
      prev.map((r) => (idSet.has(r.id) ? { ...r, project_budget: Number(r.current_budget) || 0 } : r))
    );
    setProjFromCurConfirm(null);
    setNumSel(null);
  };

  /** 추경 후 예산이 입력된 행 (숨김 행 포함) */
  const suppApplyTargetRowIds = useMemo(
    () => rows.filter((r) => r.supp_budget !== null && r.supp_budget !== undefined).map((r) => r.id),
    [rows]
  );
  const [suppApplyConfirm, setSuppApplyConfirm] = useState(false);
  const [suppHistoryRowId, setSuppHistoryRowId] = useState<string | null>(null);

  const applySuppToCurrent = () => {
    const round = nextBudgetSuppApplyRound(rows);
    const appliedAt = toISODate(new Date());
    recordBudgetNoteEditRef.current('discrete');
    setRows((prev) =>
      prev.map((r) => {
        if (r.supp_budget === null || r.supp_budget === undefined) return r;
        const before = Number(r.current_budget) || 0;
        const after = Number(r.supp_budget) || 0;
        return {
          ...r,
          current_budget: after,
          supp_budget: null,
          supp_delta_formula: null,
          supp_apply_history: [...(r.supp_apply_history ?? []), { round, before, after, applied_at: appliedAt }],
        };
      })
    );
    setSuppApplyConfirm(false);
    setNumSel(null);
  };

  const numTdSelClass = (row: number, col: BudgetNumColKey): string => {
    if (!numSelRect || !numSelIsMulti) return '';
    const c = numCols.indexOf(col);
    if (row < numSelRect.r0 || row > numSelRect.r1 || c < numSelRect.c0 || c > numSelRect.c1) return '';
    return [
      'cp-budget-note-td--num-selected',
      row === numSelRect.r0 ? 'is-sel-top' : '',
      row === numSelRect.r1 ? 'is-sel-bottom' : '',
      c === numSelRect.c0 ? 'is-sel-left' : '',
      c === numSelRect.c1 ? 'is-sel-right' : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  const numTdProps = (row: number, col: BudgetNumColKey) => ({
    'data-bn-num': `${row}:${col}`,
    onMouseDown: (e: React.MouseEvent) => handleNumCellMouseDown(row, col, e),
    onMouseEnter: () => handleNumCellMouseEnter(row, col),
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
    onDrop: (e: React.DragEvent) => e.preventDefault(),
  });

  useEffect(() => {
    const onUp = () => {
      numDragActiveRef.current = false;
      document.body.classList.remove('cp-bn-num-dragging');
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t?.closest('[data-bn-num]') ||
        t?.closest('.cp-budget-note-sum-pop') ||
        t?.closest('[data-bn-keep-num-sel]')
      ) {
        return;
      }
      setNumSel(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNumSel(null);
    };
    document.addEventListener('mouseup', onUp, true);
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('cp-bn-num-dragging');
    };
  }, []);

  useEffect(() => {
    setNumSel(null);
  }, [layoutMode, suppDataVisible]);

  useLayoutEffect(() => {
    if (!numSelRect || !numSelIsMulti) {
      setNumSumPopPos(null);
      return;
    }
    const place = () => {
      const first = document.querySelector<HTMLElement>(
        `[data-bn-num="${numSelRect.r0}:${numCols[numSelRect.c0]}"]`
      );
      const last = document.querySelector<HTMLElement>(
        `[data-bn-num="${numSelRect.r1}:${numCols[numSelRect.c1]}"]`
      );
      if (!first || !last) {
        setNumSumPopPos(null);
        return;
      }
      const a = first.getBoundingClientRect();
      const b = last.getBoundingClientRect();
      const union = new DOMRect(a.left, a.top, b.right - a.left, b.bottom - a.top);
      setNumSumPopPos(
        computeZoomAwareFixedPopupStyle({
          anchorRect: union,
          preferredWidth: 180,
          estimatedHeight: 60,
          gap: 6,
          pad: 8,
          zIndex: 96,
          placement: 'auto',
          alignX: 'center',
        })
      );
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [numSelRect, numSelIsMulti, numCols]);

  const [usageHover, setUsageHover] = useState<null | { entry: BudgetNoteUsageEntryDraft; style: CSSProperties }>(
    null
  );
  const hoverLeaveTimerRef = useRef<number | null>(null);

  const clearHoverLeaveTimer = useCallback(() => {
    if (hoverLeaveTimerRef.current != null) {
      window.clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearHoverLeaveTimer(), [clearHoverLeaveTimer]);

  useEffect(() => {
    const onZoomChanged = () => setUsageHover(null);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onZoomChanged);
    return () => window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onZoomChanged);
  }, []);

  const onUsageBlockEnter = (en: BudgetNoteUsageEntryDraft, el: HTMLElement) => {
    if (searchQuery && textIncludesBudgetSearchQuery(en.memo, searchQuery)) return;
    clearHoverLeaveTimer();
    const r = el.getBoundingClientRect();
    setUsageHover({
      entry: en,
      style: computeZoomAwareFixedPopupStyle({
        anchorRect: r,
        preferredWidth: 168,
        estimatedHeight: 120,
        gap: 6,
        pad: 8,
        zIndex: 95,
        placement: 'auto',
      }),
    });
  };

  const onUsageBlockLeave = () => {
    clearHoverLeaveTimer();
    hoverLeaveTimerRef.current = window.setTimeout(() => setUsageHover(null), 160);
  };

  const onFlyPopEnter = () => clearHoverLeaveTimer();
  const onFlyPopLeave = () => {
    hoverLeaveTimerRef.current = window.setTimeout(() => setUsageHover(null), 120);
  };

  const captureBudgetNoteHistoryFrame = useCallback((): BudgetNoteHistoryFrame => {
    return {
      rows: rowsRef.current.map((r) => ({ ...r })),
      entries: entriesRef.current.map((e) => ({ ...e })),
      deletedRowIds: [...deletedRowIdsRef.current],
      deletedEntryIds: [...deletedEntryIdsRef.current],
      categoryHeaderLabels: normalizeBudgetNoteCategoryHeaderLabels(categoryHeaderLabelsRef.current),
    };
  }, []);

  const pushUndoSnapshot = useCallback((frame: BudgetNoteHistoryFrame) => {
    const stack = undoStackRef.current;
    stack.push(cloneBudgetNoteHistoryFrame(frame));
    if (stack.length > BUDGET_NOTE_UNDO_HISTORY_MAX) stack.shift();
  }, []);

  const endHistoryCoalesce = useCallback(() => {
    historyCoalescePendingRef.current = false;
    historyCoalesceTimerRef.current = null;
  }, []);

  const scheduleHistoryCoalesceEnd = useCallback(() => {
    if (historyCoalesceTimerRef.current != null) {
      window.clearTimeout(historyCoalesceTimerRef.current);
    }
    historyCoalesceTimerRef.current = window.setTimeout(() => {
      endHistoryCoalesce();
    }, BUDGET_NOTE_UNDO_COALESCE_MS);
  }, [endHistoryCoalesce]);

  const clearBudgetNoteHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    historyCoalescePendingRef.current = false;
    if (historyCoalesceTimerRef.current != null) {
      window.clearTimeout(historyCoalesceTimerRef.current);
      historyCoalesceTimerRef.current = null;
    }
    setHistoryRevision((v) => v + 1);
  }, []);

  const applyBudgetNoteHistoryFrame = useCallback((frame: BudgetNoteHistoryFrame) => {
    isApplyingHistoryRef.current = true;
    const cloned = cloneBudgetNoteHistoryFrame(frame);
    setRows(cloned.rows);
    setEntries(cloned.entries);
    setDeletedRowIds(cloned.deletedRowIds);
    setDeletedEntryIds(cloned.deletedEntryIds);
    setCategoryHeaderLabels(cloned.categoryHeaderLabels);
    setSelectedMetaCells([]);
    setRowMenu(null);
    setUsageHover(null);
    setRowDeleteConfirm(null);
    queueMicrotask(() => {
      isApplyingHistoryRef.current = false;
    });
  }, []);

  const recordBudgetNoteEdit = useCallback(
    (mode: 'coalesce' | 'discrete' = 'discrete') => {
      if (isApplyingHistoryRef.current) return;
      if (mode === 'coalesce') {
        if (historyCoalescePendingRef.current) return;
        pushUndoSnapshot(captureBudgetNoteHistoryFrame());
        redoStackRef.current = [];
        historyCoalescePendingRef.current = true;
        scheduleHistoryCoalesceEnd();
        setHistoryRevision((v) => v + 1);
        return;
      }
      if (historyCoalesceTimerRef.current != null) {
        window.clearTimeout(historyCoalesceTimerRef.current);
        historyCoalesceTimerRef.current = null;
      }
      historyCoalescePendingRef.current = false;
      pushUndoSnapshot(captureBudgetNoteHistoryFrame());
      redoStackRef.current = [];
      setHistoryRevision((v) => v + 1);
    },
    [captureBudgetNoteHistoryFrame, pushUndoSnapshot, scheduleHistoryCoalesceEnd]
  );

  const undoBudgetNoteEdit = useCallback(() => {
    if (historyCoalesceTimerRef.current != null) {
      window.clearTimeout(historyCoalesceTimerRef.current);
      historyCoalesceTimerRef.current = null;
    }
    historyCoalescePendingRef.current = false;
    const undo = undoStackRef.current;
    if (!undo.length) return;
    redoStackRef.current.push(cloneBudgetNoteHistoryFrame(captureBudgetNoteHistoryFrame()));
    if (redoStackRef.current.length > BUDGET_NOTE_UNDO_HISTORY_MAX) redoStackRef.current.shift();
    const target = undo.pop()!;
    applyBudgetNoteHistoryFrame(target);
    setHistoryRevision((v) => v + 1);
  }, [applyBudgetNoteHistoryFrame, captureBudgetNoteHistoryFrame]);

  const redoBudgetNoteEdit = useCallback(() => {
    if (historyCoalesceTimerRef.current != null) {
      window.clearTimeout(historyCoalesceTimerRef.current);
      historyCoalesceTimerRef.current = null;
    }
    historyCoalescePendingRef.current = false;
    const redo = redoStackRef.current;
    if (!redo.length) return;
    undoStackRef.current.push(cloneBudgetNoteHistoryFrame(captureBudgetNoteHistoryFrame()));
    if (undoStackRef.current.length > BUDGET_NOTE_UNDO_HISTORY_MAX) undoStackRef.current.shift();
    const target = redo.pop()!;
    applyBudgetNoteHistoryFrame(target);
    setHistoryRevision((v) => v + 1);
  }, [applyBudgetNoteHistoryFrame, captureBudgetNoteHistoryFrame]);

  recordBudgetNoteEditRef.current = recordBudgetNoteEdit;
  undoBudgetNoteEditRef.current = undoBudgetNoteEdit;
  redoBudgetNoteEditRef.current = redoBudgetNoteEdit;
  clearBudgetNoteHistoryRef.current = clearBudgetNoteHistory;

  const canUndoBudgetNote = useMemo(() => undoStackRef.current.length > 0, [historyRevision]);
  const canRedoBudgetNote = useMemo(() => redoStackRef.current.length > 0, [historyRevision]);

  useEffect(() => {
    return () => {
      if (historyCoalesceTimerRef.current != null) {
        window.clearTimeout(historyCoalesceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (loading || saving) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoBudgetNoteEditRef.current();
        return;
      }
      if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        redoBudgetNoteEditRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [loading, saving]);

  const pub = process.env.PUBLIC_URL || '';
  const todayIso = toISODate(new Date());

  useEffect(() => {
    if (!rowMenu) return;
    const onDocDown = (e: MouseEvent) => {
      const pop = document.querySelector('.cp-budget-note-row-menu-pop');
      if (pop && pop.contains(e.target as Node)) return;
      setRowMenu(null);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [rowMenu]);

  /** 아래 공간이 부족하면 트리거 위로 올려 화면 밖으로 나가지 않게 함 */
  useLayoutEffect(() => {
    if (!rowMenu) return;
    const pop = document.querySelector('.cp-budget-note-row-menu-pop') as HTMLElement | null;
    if (!pop) return;
    const h = pop.getBoundingClientRect().height || BUDGET_ROW_MENU_EST_HEIGHT_PX;
    const vh = window.innerHeight;
    const spaceBelow = vh - rowMenu.anchorBottom - BUDGET_ROW_MENU_GAP_PX;
    const nextTop =
      spaceBelow < h + 8
        ? Math.max(8, rowMenu.anchorTop - h - BUDGET_ROW_MENU_GAP_PX)
        : rowMenu.anchorBottom + BUDGET_ROW_MENU_GAP_PX;
    if (Math.abs(nextTop - rowMenu.top) > 1) {
      setRowMenu((m) => (m && m.rowId === rowMenu.rowId ? { ...m, top: nextTop } : m));
    }
    // top 보정만 하므로 rowId·앵커 변경 시에만 재측정
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowMenu.top 변경으로 재진입하지 않음
  }, [rowMenu?.rowId, rowMenu?.anchorTop, rowMenu?.anchorBottom]);

  useEffect(() => {
    if (!metaColsMenu) return;
    const onDocDown = (e: MouseEvent) => {
      const pop = document.querySelector('.cp-budget-note-meta-cols-menu-pop');
      if (pop && pop.contains(e.target as Node)) return;
      const trigger = (e.target as HTMLElement | null)?.closest?.('.cp-budget-note-super-meta-btn');
      if (trigger) return;
      setMetaColsMenu(null);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [metaColsMenu]);

  const openMetaColsMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const menuApproxW = 520;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
    const left = Math.max(8, Math.min(r.left, vw - menuApproxW - 8));
    const top = r.bottom + 4;
    setMetaColsMenu((m) => (m ? null : { left, top }));
  }, []);

  const toggleMetaColVisible = useCallback((key: BudgetMetaColKey) => {
    setMetaColsVisible((v) => ({ ...v, [key]: !v[key] }));
  }, []);

  return (
    <section
      className={`cp-budget-note-page is-layout-${layoutMode}`}
      aria-labelledby="cp-budget-note-title"
    >
      <AcademicYearLockNotice label="예산노트" />
      <div className="cp-budget-note-sticky-top">
        <div className="cp-budget-note-header">
          <div className="cp-todo-note-title-row">
            <h2 id="cp-budget-note-title" className="cp-budget-note-title">
              예산 노트
              {variant === 'account' ? (
                <span className="cp-todo-note-title-scope"> · 계정</span>
              ) : null}
            </h2>
            <div className="cp-todo-note-title-row-controls">
              <div
                className="cp-todo-note-view-toggle"
                role="tablist"
                aria-label="표시 모드"
                data-tutorial-bn="view-toggle"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={layoutMode === 'horizontal'}
                  className={`cp-todo-note-view-btn${layoutMode === 'horizontal' ? ' is-active' : ''}`}
                  data-tutorial-bn="view-horizontal"
                  onClick={() => setLayoutMode('horizontal')}
                >
                  가로모드
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={layoutMode === 'vertical'}
                  className={`cp-todo-note-view-btn${layoutMode === 'vertical' ? ' is-active' : ''}`}
                  onClick={() => {
                    setMetaColsMenu(null);
                    setLayoutMode('vertical');
                  }}
                >
                  세로모드
                </button>
              </div>
              <div className="cp-note-header-undo-redo-mobile">
                <ProgramToolbarUndoRedoButtons
                  canUndo={canUndoBudgetNote}
                  canRedo={canRedoBudgetNote}
                  disabled={loading || saving}
                  onUndo={undoBudgetNoteEdit}
                  onRedo={redoBudgetNoteEdit}
                />
              </div>
            </div>
          </div>
          <div className="cp-budget-note-header-actions">
            <div className="cp-budget-note-search-bar" role="search">
              <div
                className={
                  'cp-budget-note-search-input-shell' +
                  (hasSearchDraftText ? ' has-clear' : '') +
                  (showSearchNavSuffix ? ' has-nav' : '')
                }
              >
                <input
                  type="text"
                  className="cp-budget-note-search-input"
                  value={searchDraft}
                  onChange={(e) => handleSearchDraftChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runBudgetSearch();
                    }
                  }}
                  placeholder="찾기"
                  aria-label="예산 노트 찾기"
                />
                {hasSearchDraftText || showSearchNavSuffix ? (
                  <div className="cp-budget-note-search-input-suffix" aria-live="polite">
                    {showSearchNavSuffix ? (
                      <>
                        <span className="cp-budget-note-search-count">
                          {searchMatches.length ? `${activeMatchIndex + 1}/${searchMatches.length}` : '0/0'}
                        </span>
                        <button
                          type="button"
                          className="cp-budget-note-search-nav-btn"
                          onClick={goPrevBudgetSearchMatch}
                          disabled={!searchMatches.length}
                          aria-label="이전 찾기 결과"
                          title="이전"
                        >
                          <svg viewBox="0 0 20 14" width="16" height="12" focusable="false" aria-hidden>
                            <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="cp-budget-note-search-nav-btn"
                          onClick={goNextBudgetSearchMatch}
                          disabled={!searchMatches.length}
                          aria-label="다음 찾기 결과"
                          title="다음"
                        >
                          <svg viewBox="0 0 20 14" width="16" height="12" focusable="false" aria-hidden>
                            <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                          </svg>
                        </button>
                      </>
                    ) : null}
                    {hasSearchDraftText ? (
                      <button
                        type="button"
                        className="cp-budget-note-search-clear-btn"
                        onClick={clearBudgetSearch}
                        aria-label="찾기 지우기"
                        title="찾기 지우기"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="cp-budget-note-search-submit"
                onClick={runBudgetSearch}
                aria-label="찾기"
                title="찾기"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" focusable="false" aria-hidden>
                  <path
                    fill="currentColor"
                    d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                  />
                </svg>
              </button>
            </div>
            <ProgramToolbarUndoRedoButtons
              canUndo={canUndoBudgetNote}
              canRedo={canRedoBudgetNote}
              disabled={loading || saving}
              onUndo={undoBudgetNoteEdit}
              onRedo={redoBudgetNoteEdit}
            />
            <button type="button" className="cp-btn-save-all" disabled={saving || !dirty} onClick={() => void handleSave()}>
              {saving ? '저장 중…' : '저장하기'}
            </button>
          </div>
        </div>
        {saveError ? <p className="cp-budget-note-error">{saveError}</p> : null}
      </div>

      {loadError ? <p className="cp-budget-note-error">{loadError}</p> : null}

      {loading ? (
        <p className="cp-budget-note-muted">불러오는 중…</p>
      ) : (
        <div className="cp-budget-note-body">
          <div className="cp-budget-note-table-toolbar">
            <div className="cp-budget-note-toolbar-color-row">
              <div
                className={
                  'cp-base-timetable-dedicated-palette cp-budget-note-mark-color-palette' +
                  (hasMetaCellSelection ? ' is-active' : '')
                }
                role="group"
                aria-label="셀 표시 색상"
                data-tutorial-bn="mark-color-palette"
              >
                <button
                  type="button"
                  className="cp-dedicated-color-btn cp-dedicated-color-none-btn"
                  onClick={() => applyMarkColorToSelected('')}
                  disabled={!hasMetaCellSelection}
                  title="색상 없음"
                  aria-label="색상 없음"
                />
                {toolbarMarkColors.map((hex) => renderMarkColorSwatchBtn(hex))}
                <button
                  type="button"
                  className="cp-dedicated-color-btn cp-dedicated-color-add-btn"
                  onClick={() => setMarkColorPickModal(true)}
                  disabled={!hasMetaCellSelection}
                  title="색상 추가"
                  aria-label="색상 추가"
                >
                  +
                </button>
              </div>
              <div className="cp-budget-note-toolbar-toggles">
                {layoutMode === 'horizontal' ? (
                  <div
                    className="cp-program-progress-toolbar-group cp-budget-note-row-order-toolbar"
                    role="group"
                    aria-label="행 순서"
                  >
                    <button
                      type="button"
                      className="cp-btn-copy-paste cp-btn-swap-region"
                      onClick={() => moveSelectedRows(-1)}
                      disabled={!hasMetaCellSelection || categorySortFilterEnabled}
                      title={categorySortFilterEnabled ? '필터를 끄면 순서를 조정할 수 있습니다' : '한칸 올리기'}
                      aria-label="한칸 올리기"
                    >
                      <span className="cp-btn-label-desktop">한칸 올리기</span>
                      <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                        <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                          <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                          <path
                            d="M14 12l3.5-3.5-1.2-1.2L15 8.6V2h-2v6.6l-1.3-1.3-1.2 1.2L14 12z"
                            fill="currentColor"
                            opacity="0.45"
                          />
                        </svg>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cp-btn-copy-paste cp-btn-swap-region"
                      onClick={() => moveSelectedRows(1)}
                      disabled={!hasMetaCellSelection || categorySortFilterEnabled}
                      title={categorySortFilterEnabled ? '필터를 끄면 순서를 조정할 수 있습니다' : '한칸 내리기'}
                      aria-label="한칸 내리기"
                    >
                      <span className="cp-btn-label-desktop">한칸 내리기</span>
                      <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                        <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                          <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                          <path
                            d="M14 2l-3.5 3.5 1.2 1.2L13 5.4V12h2V5.4l1.3 1.3 1.2-1.2L14 2z"
                            fill="currentColor"
                            opacity="0.45"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                ) : null}
                <div
                  className="cp-budget-note-toolbar-switch-group"
                  role="group"
                  aria-label="필터·숨김·추경 표시"
                  data-tutorial-bn="toolbar-toggles"
                >
                  <div className="cp-annual-top-saturday-toggle cp-budget-note-filter-toggle" aria-label="카테고리 정렬 필터">
                    <span className="cp-weekly-schedule-saturday-toggle-label">필터</span>
                    <button
                      type="button"
                      className={'team-auto-approve-toggle' + (categorySortFilterEnabled ? ' on' : '')}
                      onClick={() => setCategorySortFilterEnabled((v) => !v)}
                      aria-pressed={categorySortFilterEnabled}
                      aria-label="카테고리 정렬 필터"
                    >
                      <span className="team-auto-approve-toggle-slider" data-on={!categorySortFilterEnabled}>
                        OFF
                      </span>
                      <span className="team-auto-approve-toggle-slider" data-on={categorySortFilterEnabled}>
                        ON
                      </span>
                    </button>
                  </div>
                  <div className="cp-annual-top-saturday-toggle cp-budget-note-hidden-toggle" aria-label="숨김 데이터 표시">
                    <span className="cp-weekly-schedule-saturday-toggle-label">
                      <span className="cp-budget-note-toggle-label-full">숨김 데이터 보이기</span>
                      <span className="cp-budget-note-toggle-label-short">숨김</span>
                    </span>
                    <button
                      type="button"
                      className={'team-auto-approve-toggle' + (hiddenDataVisible ? ' on' : '')}
                      onClick={() => setHiddenDataVisible((v) => !v)}
                      aria-pressed={hiddenDataVisible}
                      aria-label="숨김 처리한 행 표시"
                    >
                      <span className="team-auto-approve-toggle-slider" data-on={!hiddenDataVisible}>
                        OFF
                      </span>
                      <span className="team-auto-approve-toggle-slider" data-on={hiddenDataVisible}>
                        ON
                      </span>
                    </button>
                  </div>
                  <div className="cp-annual-top-saturday-toggle cp-budget-note-supp-toggle" aria-label="추경 데이터 표시">
                    <span className="cp-weekly-schedule-saturday-toggle-label">
                      <span className="cp-budget-note-toggle-label-full">추경 데이터 보이기</span>
                      <span className="cp-budget-note-toggle-label-short">추경</span>
                    </span>
                    <button
                      type="button"
                      className={'team-auto-approve-toggle' + (suppDataVisible ? ' on' : '')}
                      onClick={() => setSuppDataVisible((v) => !v)}
                      aria-pressed={suppDataVisible}
                      aria-label="추경 예산 관련 열 표시"
                    >
                      <span className="team-auto-approve-toggle-slider" data-on={!suppDataVisible}>
                        OFF
                      </span>
                      <span className="team-auto-approve-toggle-slider" data-on={suppDataVisible}>
                        ON
                      </span>
                    </button>
                  </div>
                  <div
                    className="cp-annual-top-saturday-toggle cp-budget-note-planned-balance-toggle"
                    aria-label="집행예정 잔액에 포함"
                  >
                    <span className="cp-weekly-schedule-saturday-toggle-label">
                      <span className="cp-budget-note-toggle-label-full">집행예정 잔액에 포함</span>
                      <span className="cp-budget-note-toggle-label-short">예정</span>
                    </span>
                    <button
                      type="button"
                      className={'team-auto-approve-toggle' + (plannedInBalanceEnabled ? ' on' : '')}
                      onClick={() => setPlannedInBalanceEnabled((v) => !v)}
                      aria-pressed={plannedInBalanceEnabled}
                      aria-label="현재잔액에 집행예정 금액 포함"
                    >
                      <span className="team-auto-approve-toggle-slider" data-on={!plannedInBalanceEnabled}>
                        OFF
                      </span>
                      <span className="team-auto-approve-toggle-slider" data-on={plannedInBalanceEnabled}>
                        ON
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {layoutMode === 'horizontal' ? (
            <div className="cp-budget-note-scroll-wrap">
              <table
                className="cp-budget-note-table"
                data-tutorial-bn="table"
                style={{ minWidth: horizontalTableMinWidthPx }}
              >
              {horizontalColgroup}
              <thead>
                <tr className="cp-budget-note-tr cp-budget-note-tr--super-head">
                  <th
                    scope="col"
                    className="cp-budget-note-th cp-budget-note-th--rowact"
                    aria-hidden
                  />
                  <th
                    colSpan={Math.max(1, budgetSuperHeaderColSpans.metaColSpan)}
                    scope="colgroup"
                    className="cp-budget-note-th cp-budget-note-th--super cp-budget-note-th--super-meta"
                    data-tutorial-bn="th-super-meta"
                  >
                    <button
                      type="button"
                      className="cp-budget-note-super-meta-btn"
                      aria-haspopup="dialog"
                      aria-expanded={Boolean(metaColsMenu)}
                      aria-label="예산 구분 및 내용 열 표시 설정"
                      title="클릭하여 열 보이기·숨기기"
                      data-tutorial-bn="open-meta-cols"
                      onClick={openMetaColsMenu}
                    >
                      예산 구분 및 내용
                    </button>
                  </th>
                  <th
                    colSpan={budgetSuperHeaderColSpans.planColSpan}
                    scope="colgroup"
                    className="cp-budget-note-th cp-budget-note-th--super cp-budget-note-th--super-plan"
                    data-tutorial-bn="th-super-plan"
                  >
                    계획 예산
                  </th>
                  <th
                    scope="col"
                    className="cp-budget-note-th cp-budget-note-th--super cp-budget-note-th--super-proj"
                    data-tutorial-bn="th-super-proj"
                  >
                    사용할 예산
                  </th>
                  <th
                    colSpan={budgetSuperHeaderColSpans.usageColSpan}
                    scope="colgroup"
                    className="cp-budget-note-th cp-budget-note-th--super cp-budget-note-th--super-usage"
                  >
                    사용액 및 잔액
                  </th>
                  <th
                    scope="col"
                    className="cp-budget-note-th cp-budget-note-th--super cp-budget-note-th--super-exec"
                  >
                    집행 기록
                  </th>
                </tr>
                <tr className="cp-budget-note-tr cp-budget-note-tr--head">
                  <th
                    scope="col"
                    className="cp-budget-note-th cp-budget-note-th--rowact"
                    aria-hidden
                  />
                  {metaColsPlaceholderVisible ? (
                    <th
                      scope="col"
                      className="cp-budget-note-th cp-budget-note-th--meta-placeholder"
                      aria-label="예산 구분 및 내용 열 없음"
                    >
                      <span className="cp-budget-note-meta-placeholder-hint">열 숨김</span>
                    </th>
                  ) : null}
                  {BUDGET_CATEGORY_KEYS.map((ck) => {
                    if (!metaColsVisible[ck]) return null;
                    const sortDir = categorySort[ck];
                    const sortIconMaskUrl =
                      sortDir === 'desc'
                        ? `${pub}/Sort%20Descending%20Line.svg`
                        : `${pub}/Sort%20Ascending%20Line.svg`;
                    return (
                      <th
                        key={ck}
                        scope="col"
                        className="cp-budget-note-th cp-budget-note-th--cat cp-budget-note-th--cat-sort"
                        data-tutorial-bn="th-cat"
                      >
                        <div className="cp-budget-note-th-cat-head">
                          {editingCategoryKey === ck ? (
                            <input
                              ref={categoryHeaderInputRef}
                              className="cp-budget-note-th-cat-title-input"
                              value={categoryHeaderDraft}
                              onChange={(e) => setCategoryHeaderDraft(e.target.value)}
                              onBlur={commitCategoryHeaderEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitCategoryHeaderEdit();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  cancelCategoryHeaderEdit();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`${categoryHeaderLabels[ck]} 열 이름 편집`}
                            />
                          ) : (
                            <span
                              className="cp-budget-note-th-cat-title"
                              title="더블클릭하여 열 이름 변경"
                              onDoubleClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                beginEditCategoryHeader(ck);
                              }}
                            >
                              {categoryHeaderLabels[ck]}
                            </span>
                          )}
                          {categorySortFilterEnabled ? (
                            <button
                              type="button"
                              className="cp-budget-note-cat-sort-btn is-active"
                              aria-label={categorySortAriaLabel(categoryHeaderLabels[ck], sortDir)}
                              aria-pressed
                              onClick={() => toggleCategorySort(ck)}
                            >
                              <span
                                className="cp-budget-note-cat-sort-icon"
                                style={{
                                  WebkitMaskImage: `url(${JSON.stringify(sortIconMaskUrl)})`,
                                  maskImage: `url(${JSON.stringify(sortIconMaskUrl)})`,
                                }}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                        <div
                          className="cp-budget-note-th-resize-handle"
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`${categoryHeaderLabels[ck]} 열 너비 조절`}
                          onPointerDown={beginMetaColResize(ck)}
                        />
                      </th>
                    );
                  })}
                  {metaColsVisible.memo ? (
                    <th
                      scope="col"
                      className="cp-budget-note-th cp-budget-note-th--memo"
                      data-tutorial-bn="th-memo"
                    >
                      메모
                      <div
                        className="cp-budget-note-th-resize-handle"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="메모 열 너비 조절"
                        onPointerDown={beginMetaColResize('memo')}
                      />
                    </th>
                  ) : null}
                  <th
                    scope="col"
                    className="cp-budget-note-th cp-budget-note-th--cur"
                    data-tutorial-bn="th-cur"
                  >
                    현재예산
                  </th>
                  {suppDataVisible ? (
                    <>
                      <th scope="col" className="cp-budget-note-th cp-budget-note-th--supp">
                        <button
                          type="button"
                          className="cp-budget-note-proj-head-btn cp-budget-note-supp-head-btn"
                          data-bn-keep-num-sel
                          disabled={suppApplyTargetRowIds.length === 0}
                          onClick={() => {
                            if (suppApplyTargetRowIds.length === 0) return;
                            setSuppApplyConfirm(true);
                          }}
                          title={
                            suppApplyTargetRowIds.length === 0
                              ? '추경 후 예산이 입력된 행이 없습니다'
                              : `추경 후 예산 ${suppApplyTargetRowIds.length}개 행을 현재예산으로 반영`
                          }
                        >
                          추경 후 예산
                        </button>
                      </th>
                      <th scope="col" className="cp-budget-note-th cp-budget-note-th--delta">
                        추경증감
                        <div
                          className="cp-budget-note-th-resize-handle"
                          role="separator"
                          aria-orientation="vertical"
                          aria-label="추경증감 열 너비 조절"
                          onPointerDown={beginDeltaColResize}
                        />
                      </th>
                    </>
                  ) : null}
                  <th
                    scope="col"
                    className="cp-budget-note-th cp-budget-note-th--proj"
                    data-tutorial-bn="th-proj"
                  >
                    <button
                      type="button"
                      className="cp-budget-note-proj-head-btn"
                      data-bn-keep-num-sel
                      disabled={projFromCurTargetRowIds.length === 0}
                      onClick={() => {
                        if (projFromCurTargetRowIds.length === 0) return;
                        setProjFromCurConfirm({ rowIds: projFromCurTargetRowIds });
                      }}
                      title={
                        projFromCurTargetRowIds.length === 0
                          ? '사업예산 열에서 셀을 선택하면 현재예산 금액을 사업예산으로 넣을 수 있습니다'
                          : `선택한 ${projFromCurTargetRowIds.length}개 행의 사업예산을 현재예산 금액으로 넣기`
                      }
                    >
                      사업예산
                    </button>
                  </th>
                  <th scope="col" className="cp-budget-note-th cp-budget-note-th--used">
                    사용액
                  </th>
                  <th scope="col" className="cp-budget-note-th cp-budget-note-th--bal">
                    현재잔액
                  </th>
                  {suppDataVisible ? (
                    <th scope="col" className="cp-budget-note-th cp-budget-note-th--suppbal">
                      추경 후 잔액
                    </th>
                  ) : null}
                  <th scope="col" className="cp-budget-note-th cp-budget-note-th--add">
                    집행
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={budgetTableEmptyColSpan} className="cp-budget-note-empty">
                      예산 항목이 없습니다. 오른쪽 아래 + 버튼으로 행을 추가하세요.
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row, rowIndex) => {
                    const cur = Number(row.current_budget) || 0;
                    const supp = row.supp_budget;
                    const proj = Number(row.project_budget) || 0;
                    const used = usedCompletedSum(row.id);
                    const suppDelta = supp !== null && supp !== undefined ? (Number(supp) || 0) - cur : null;
                    const plannedForBal = plannedInBalanceEnabled ? plannedSum(row.id) : 0;
                    const usedShown = used + plannedForBal;
                    const curBal = proj - usedShown;
                    const suppBal =
                      supp !== null && supp !== undefined ? (Number(supp) || 0) - usedShown : null;
                    const list = entriesForRow(row.id);

                    return (
                      <tr
                        key={row.id}
                        className={'cp-budget-note-tr' + (row.is_hidden ? ' is-hidden-row' : '')}
                        data-tutorial-bn={rowIndex === 0 ? 'first-row' : undefined}
                      >
                        <td className="cp-budget-note-td cp-budget-note-td--rowact">
                          <button
                            type="button"
                            className="cp-budget-note-row-menu-trigger"
                            aria-haspopup="dialog"
                            aria-expanded={rowMenu?.rowId === row.id}
                            aria-label={`${categoryHeaderLabels.cat1}~${categoryHeaderLabels.cat4} 행 메뉴`}
                            data-tutorial-bn={rowIndex === 0 ? 'row-menu-trigger' : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              const el = e.currentTarget;
                              setRowMenu((m) =>
                                m?.rowId === row.id ? null : buildBudgetRowMenuState(row.id, el)
                              );
                            }}
                          >
                            <svg className="cp-budget-note-row-menu-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                              <path
                                fill="currentColor"
                                d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"
                              />
                            </svg>
                          </button>
                        </td>
                        {metaColsPlaceholderVisible ? (
                          <td className="cp-budget-note-td cp-budget-note-td--meta-placeholder" aria-hidden />
                        ) : null}
                        {metaColsVisible.cat1
                          ? renderHorizontalMetaCell(
                              row,
                              rowIndex,
                              'cat1',
                              'cp-budget-note-td--cat',
                              `${categoryHeaderLabels.cat1} (최상위)`,
                              row.cat1,
                              row.cat1_color,
                              (v) => patchRow(row.id, { cat1: v }),
                              handleBudgetInputPaste(rowIndex, 'cat1')
                            )
                          : null}
                        {metaColsVisible.cat2
                          ? renderHorizontalMetaCell(
                              row,
                              rowIndex,
                              'cat2',
                              'cp-budget-note-td--cat',
                              categoryHeaderLabels.cat2,
                              row.cat2,
                              row.cat2_color,
                              (v) => patchRow(row.id, { cat2: v }),
                              handleBudgetInputPaste(rowIndex, 'cat2')
                            )
                          : null}
                        {metaColsVisible.cat3
                          ? renderHorizontalMetaCell(
                              row,
                              rowIndex,
                              'cat3',
                              'cp-budget-note-td--cat',
                              categoryHeaderLabels.cat3,
                              row.cat3,
                              row.cat3_color,
                              (v) => patchRow(row.id, { cat3: v }),
                              handleBudgetInputPaste(rowIndex, 'cat3')
                            )
                          : null}
                        {metaColsVisible.cat4
                          ? renderHorizontalMetaCell(
                              row,
                              rowIndex,
                              'cat4',
                              'cp-budget-note-td--cat',
                              `${categoryHeaderLabels.cat4} (최하위)`,
                              row.cat4,
                              row.cat4_color,
                              (v) => patchRow(row.id, { cat4: v }),
                              handleBudgetInputPaste(rowIndex, 'cat4')
                            )
                          : null}
                        {metaColsVisible.memo
                          ? renderHorizontalMetaCell(
                              row,
                              rowIndex,
                              'memo',
                              'cp-budget-note-td--memo',
                              '메모',
                              row.memo,
                              row.memo_color,
                              (v) => patchRow(row.id, { memo: v }),
                              handleBudgetInputPaste(rowIndex, 'memo')
                            )
                          : null}
                        <td
                          className={`cp-budget-note-td cp-budget-note-td--cur ${
                            row.supp_apply_history?.length ? 'cp-budget-note-td--cur-has-supp-history' : ''
                          } ${numTdSelClass(rowIndex, 'cur')}`}
                          {...numTdProps(rowIndex, 'cur')}
                        >
                          <input
                            className="cp-budget-note-input cp-budget-note-input--num cp-budget-note-input--single"
                            inputMode="numeric"
                            value={row.current_budget === 0 ? '' : formatMoney(cur)}
                            onChange={(e) => patchRow(row.id, { current_budget: parseMoneyInput(e.target.value) })}
                            onPaste={handleBudgetInputPaste(rowIndex, 'current_budget')}
                            aria-label="현재예산"
                          />
                          {row.supp_apply_history?.length ? (
                            <button
                              type="button"
                              className="cp-budget-note-supp-history-mark"
                              title="추경 반영 기록 보기"
                              aria-label="추경 반영 기록 보기"
                              onPointerDown={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSuppHistoryRowId(row.id);
                              }}
                            />
                          ) : null}
                        </td>
                        {suppDataVisible ? (
                          <>
                            <td className={`cp-budget-note-td cp-budget-note-td--supp ${numTdSelClass(rowIndex, 'supp')}`} {...numTdProps(rowIndex, 'supp')}>
                              <input
                                className="cp-budget-note-input cp-budget-note-input--num cp-budget-note-input--single"
                                inputMode="numeric"
                                value={supp === null || supp === undefined ? '' : formatMoney(Number(supp) || 0)}
                                onChange={(e) => {
                                  const raw = e.target.value.trim();
                                  if (raw === '') patchRow(row.id, { supp_budget: null });
                                  else patchRow(row.id, { supp_budget: parseMoneyInput(raw) });
                                }}
                                onPaste={handleBudgetInputPaste(rowIndex, 'supp_budget')}
                                aria-label="추경 후 예산"
                              />
                            </td>
                            <td className={`cp-budget-note-td cp-budget-note-td--delta ${numTdSelClass(rowIndex, 'delta')}`} {...numTdProps(rowIndex, 'delta')}>
                              <BudgetSupplementDeltaInput
                                className="cp-budget-note-input cp-budget-note-input--num cp-budget-note-input--single"
                                delta={suppDelta}
                                formula={row.supp_delta_formula}
                                onChangeDelta={(d, f) =>
                                  patchRow(row.id, { supp_budget: d === null ? null : cur + d, supp_delta_formula: f })
                                }
                              />
                            </td>
                          </>
                        ) : null}
                        <td className={`cp-budget-note-td cp-budget-note-td--proj ${numTdSelClass(rowIndex, 'proj')}`} {...numTdProps(rowIndex, 'proj')}>
                          <input
                            className="cp-budget-note-input cp-budget-note-input--num cp-budget-note-input--single"
                            inputMode="numeric"
                            value={row.project_budget === 0 ? '' : formatMoney(proj)}
                            onChange={(e) => patchRow(row.id, { project_budget: parseMoneyInput(e.target.value) })}
                            onPaste={handleBudgetInputPaste(rowIndex, 'project_budget')}
                            aria-label="사업예산"
                          />
                        </td>
                        <td
                          className={`cp-budget-note-td cp-budget-note-td--used cp-budget-note-td--readonly ${
                            plannedForBal > 0 ? 'cp-budget-note-td--with-planned' : ''
                          } ${numTdSelClass(rowIndex, 'used')}`}
                          title={plannedForBal > 0 ? `집행예정 ${formatMoney(plannedForBal)}원 포함` : undefined}
                          {...numTdProps(rowIndex, 'used')}
                        >
                          {formatMoney(usedShown)}
                        </td>
                        <td className={`cp-budget-note-td cp-budget-note-td--bal cp-budget-note-td--readonly ${numTdSelClass(rowIndex, 'bal')}`} {...numTdProps(rowIndex, 'bal')}>
                          <div
                            className={
                              'cp-budget-note-bal-cell' + (plannedForBal > 0 ? ' cp-budget-note-bal-cell--with-planned' : '')
                            }
                            title={plannedForBal > 0 ? `집행예정 ${formatMoney(plannedForBal)}원 포함` : undefined}
                          >
                            <span
                              className={'cp-budget-note-bal-value' + (curBal < 0 ? ' cp-budget-note-bal-value--negative' : '')}
                            >
                              {formatMoney(curBal)}
                            </span>
                            {renderBudgetBalanceGauge(proj, curBal)}
                          </div>
                        </td>
                        {suppDataVisible ? (
                          <td
                            className={`cp-budget-note-td cp-budget-note-td--suppbal cp-budget-note-td--readonly ${
                              plannedForBal > 0 && suppBal !== null ? 'cp-budget-note-td--with-planned' : ''
                            } ${numTdSelClass(rowIndex, 'suppbal')}`}
                            title={
                              plannedForBal > 0 && suppBal !== null
                                ? `집행예정 ${formatMoney(plannedForBal)}원 포함`
                                : undefined
                            }
                            {...numTdProps(rowIndex, 'suppbal')}
                          >
                            {renderSupplementBalance(suppBal)}
                          </td>
                        ) : null}
                        <td className="cp-budget-note-td cp-budget-note-td--strip">
                          <div className="cp-budget-note-strip-scroll">
                            <div className="cp-budget-note-strip">
                              <button
                                type="button"
                                className="cp-budget-note-row-add"
                                aria-label="집행 내역 추가"
                                data-tutorial-bn="row-add-usage"
                                onClick={() => openUsageAddModal(row.id)}
                              >
                                +
                              </button>
                              {list.map((en) => {
                                const usageMemoSearchHit =
                                  Boolean(searchQuery) && textIncludesBudgetSearchQuery(en.memo, searchQuery);
                                return (
                                <div
                                  key={en.id}
                                  className={
                                    'cp-budget-note-block-wrap' +
                                    (usageMemoSearchHit ? ' cp-budget-note-block-wrap--search-hit' : '')
                                  }
                                  onMouseEnter={(e) => onUsageBlockEnter(en, e.currentTarget)}
                                  onMouseLeave={onUsageBlockLeave}
                                >
                                  <button
                                    type="button"
                                    className={`cp-budget-note-block${en.kind === 'planned' ? ' cp-budget-note-block--planned' : ' cp-budget-note-block--done'}`}
                                    onClick={() => openUsageEditModal(en.id)}
                                    aria-label={`집행 ${formatMoney(Number(en.amount) || 0)} 세부보기·클릭하여 수정·삭제`}
                                  >
                                    <span className="cp-budget-note-block-amt-only">
                                      {formatMoney(Number(en.amount) || 0)}
                                    </span>
                                  </button>
                                  {usageMemoSearchHit ? (
                                    <div
                                      className="cp-budget-note-fly-pop cp-budget-note-fly-pop--search-pinned"
                                      role="status"
                                    >
                                      <BudgetNoteUsageFlyPopBody
                                        entry={en}
                                        searchQuery={searchQuery}
                                        activeMatchId={activeMatchId}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              );
                              })}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="cp-budget-note-vert-outer">
              {displayRows.length === 0 ? (
                <p className="cp-budget-note-empty cp-budget-note-empty--vert">
                  예산 항목이 없습니다. 오른쪽 아래 + 버튼으로 행을 추가하세요.
                </p>
              ) : (
                <div ref={vertCardsScrollRef} className="cp-budget-note-vert-cards-scroll">
                  <div className="cp-budget-note-vert-cards-row">
                    {displayRows.map((row, rowIndex) => {
                  const cur = Number(row.current_budget) || 0;
                  const supp = row.supp_budget;
                  const proj = Number(row.project_budget) || 0;
                  const used = usedCompletedSum(row.id);
                  const suppDelta = supp !== null && supp !== undefined ? (Number(supp) || 0) - cur : null;
                  const plannedForBal = plannedInBalanceEnabled ? plannedSum(row.id) : 0;
                  const usedShown = used + plannedForBal;
                  const curBal = proj - usedShown;
                  const suppBal =
                    supp !== null && supp !== undefined ? (Number(supp) || 0) - usedShown : null;
                  const listVert = [...entriesForRow(row.id)].sort((a, b) => {
                    const c = compareBudgetUsageForVertical(a, b, todayIso);
                    if (c !== 0) return c;
                    return a.sort_order - b.sort_order || a.id.localeCompare(b.id);
                  });
                  const vertIn = 'cp-budget-note-input cp-budget-note-input--single cp-budget-note-vert-input';
                  return (
                    <article
                      key={row.id}
                      className={'cp-budget-note-vert-card' + (row.is_hidden ? ' is-hidden-row' : '')}
                    >
                      <div className="cp-budget-note-vert-card-head">
                        <button
                          type="button"
                          className="cp-budget-note-row-menu-trigger"
                          aria-haspopup="dialog"
                          aria-expanded={rowMenu?.rowId === row.id}
                          aria-label={`${categoryHeaderLabels.cat1}~${categoryHeaderLabels.cat4} 행 메뉴`}
                          data-tutorial-bn={rowIndex === 0 ? 'row-menu-trigger' : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            const el = e.currentTarget;
                            setRowMenu((m) =>
                              m?.rowId === row.id ? null : buildBudgetRowMenuState(row.id, el)
                            );
                          }}
                        >
                          <svg className="cp-budget-note-row-menu-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                            <path
                              fill="currentColor"
                              d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="cp-budget-note-vert-fields">
                        {metaColsVisible.cat1
                          ? renderVerticalMetaField(
                              row.id,
                              'cat1',
                              categoryHeaderLabels.cat1,
                              `${categoryHeaderLabels.cat1} (최상위)`,
                              row.cat1,
                              row.cat1_color,
                              (v) => patchRow(row.id, { cat1: v }),
                              handleBudgetInputPaste(rowIndex, 'cat1')
                            )
                          : null}
                        {metaColsVisible.cat2
                          ? renderVerticalMetaField(
                              row.id,
                              'cat2',
                              categoryHeaderLabels.cat2,
                              categoryHeaderLabels.cat2,
                              row.cat2,
                              row.cat2_color,
                              (v) => patchRow(row.id, { cat2: v }),
                              handleBudgetInputPaste(rowIndex, 'cat2')
                            )
                          : null}
                        {metaColsVisible.cat3
                          ? renderVerticalMetaField(
                              row.id,
                              'cat3',
                              categoryHeaderLabels.cat3,
                              categoryHeaderLabels.cat3,
                              row.cat3,
                              row.cat3_color,
                              (v) => patchRow(row.id, { cat3: v }),
                              handleBudgetInputPaste(rowIndex, 'cat3')
                            )
                          : null}
                        {metaColsVisible.cat4
                          ? renderVerticalMetaField(
                              row.id,
                              'cat4',
                              categoryHeaderLabels.cat4,
                              `${categoryHeaderLabels.cat4} (최하위)`,
                              row.cat4,
                              row.cat4_color,
                              (v) => patchRow(row.id, { cat4: v }),
                              handleBudgetInputPaste(rowIndex, 'cat4')
                            )
                          : null}
                        {metaColsVisible.memo
                          ? renderVerticalMetaField(
                              row.id,
                              'memo',
                              BUDGET_META_COL_LABELS.memo,
                              '메모',
                              row.memo,
                              row.memo_color,
                              (v) => patchRow(row.id, { memo: v }),
                              handleBudgetInputPaste(rowIndex, 'memo')
                            )
                          : null}
                        <div className="cp-budget-note-vert-field cp-budget-note-vert-field--cur">
                          <span className="cp-budget-note-vert-label">
                            현재예산
                            {row.supp_apply_history?.length ? (
                              <button
                                type="button"
                                className="cp-budget-note-supp-history-chip"
                                onClick={() => setSuppHistoryRowId(row.id)}
                              >
                                추경 기록
                              </button>
                            ) : null}
                          </span>
                          <input
                            className={`${vertIn} cp-budget-note-input--num`}
                            inputMode="numeric"
                            value={row.current_budget === 0 ? '' : formatMoney(cur)}
                            onChange={(e) => patchRow(row.id, { current_budget: parseMoneyInput(e.target.value) })}
                            onPaste={handleBudgetInputPaste(rowIndex, 'current_budget')}
                            aria-label="현재예산"
                          />
                        </div>
                        {suppDataVisible ? (
                          <>
                            <div className="cp-budget-note-vert-field cp-budget-note-vert-field--supp">
                              <span className="cp-budget-note-vert-label">추경 후 예산</span>
                              <input
                                className={`${vertIn} cp-budget-note-input--num`}
                                inputMode="numeric"
                                value={supp === null || supp === undefined ? '' : formatMoney(Number(supp) || 0)}
                                onChange={(e) => {
                                  const raw = e.target.value.trim();
                                  if (raw === '') patchRow(row.id, { supp_budget: null });
                                  else patchRow(row.id, { supp_budget: parseMoneyInput(raw) });
                                }}
                                onPaste={handleBudgetInputPaste(rowIndex, 'supp_budget')}
                                aria-label="추경 후 예산"
                              />
                            </div>
                            <div className="cp-budget-note-vert-field cp-budget-note-vert-field--delta">
                              <span className="cp-budget-note-vert-label">추경증감</span>
                              <BudgetSupplementDeltaInput
                                className={`${vertIn} cp-budget-note-input--num`}
                                delta={suppDelta}
                                formula={row.supp_delta_formula}
                                onChangeDelta={(d, f) =>
                                  patchRow(row.id, { supp_budget: d === null ? null : cur + d, supp_delta_formula: f })
                                }
                              />
                            </div>
                          </>
                        ) : null}
                        <div className="cp-budget-note-vert-field cp-budget-note-vert-field--proj">
                          <span className="cp-budget-note-vert-label">사업예산</span>
                          <input
                            className={`${vertIn} cp-budget-note-input--num`}
                            inputMode="numeric"
                            value={row.project_budget === 0 ? '' : formatMoney(proj)}
                            onChange={(e) => patchRow(row.id, { project_budget: parseMoneyInput(e.target.value) })}
                            onPaste={handleBudgetInputPaste(rowIndex, 'project_budget')}
                            aria-label="사업예산"
                          />
                        </div>
                        <div className="cp-budget-note-vert-field cp-budget-note-vert-field--used">
                          <span className="cp-budget-note-vert-label">사용액</span>
                          <span
                            className={
                              'cp-budget-note-vert-readonly' +
                              (plannedForBal > 0 ? ' cp-budget-note-vert-readonly--with-planned' : '')
                            }
                            title={plannedForBal > 0 ? `집행예정 ${formatMoney(plannedForBal)}원 포함` : undefined}
                          >
                            {formatMoney(usedShown)}
                          </span>
                        </div>
                        <div className="cp-budget-note-vert-field cp-budget-note-vert-field--bal">
                          <span className="cp-budget-note-vert-label">현재잔액</span>
                          <div
                            className={
                              'cp-budget-note-bal-cell' + (plannedForBal > 0 ? ' cp-budget-note-bal-cell--with-planned' : '')
                            }
                            title={plannedForBal > 0 ? `집행예정 ${formatMoney(plannedForBal)}원 포함` : undefined}
                          >
                            <span
                              className={
                                'cp-budget-note-vert-readonly' + (curBal < 0 ? ' cp-budget-note-bal-value--negative' : '')
                              }
                            >
                              {formatMoney(curBal)}
                            </span>
                            {renderBudgetBalanceGauge(proj, curBal)}
                          </div>
                        </div>
                        {suppDataVisible ? (
                          <div className="cp-budget-note-vert-field cp-budget-note-vert-field--suppbal">
                            <span className="cp-budget-note-vert-label">추경 후 잔액</span>
                            <span
                              className={
                                'cp-budget-note-vert-readonly' +
                                (plannedForBal > 0 && suppBal !== null ? ' cp-budget-note-vert-readonly--with-planned' : '')
                              }
                              title={
                                plannedForBal > 0 && suppBal !== null
                                  ? `집행예정 ${formatMoney(plannedForBal)}원 포함`
                                  : undefined
                              }
                            >
                              {renderSupplementBalance(suppBal)}
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <hr className="cp-budget-note-vert-rule" aria-hidden />
                      <div className="cp-budget-note-vert-strip-inner">
                        <div className="cp-budget-note-strip cp-budget-note-strip--vert-col">
                          <button
                            type="button"
                            className="cp-budget-note-row-add cp-budget-note-row-add--vert-col"
                            aria-label="집행 내역 추가"
                            data-tutorial-bn="row-add-usage"
                            onClick={() => openUsageAddModal(row.id)}
                          >
                            +
                          </button>
                          {listVert.map((en) => {
                            const usageMemoSearchHit =
                              Boolean(searchQuery) && textIncludesBudgetSearchQuery(en.memo, searchQuery);
                            return (
                            <div
                              key={en.id}
                              className={
                                'cp-budget-note-block-wrap cp-budget-note-block-wrap--vert-col' +
                                (usageMemoSearchHit ? ' cp-budget-note-block-wrap--search-hit' : '')
                              }
                              onMouseEnter={(e) => onUsageBlockEnter(en, e.currentTarget)}
                              onMouseLeave={onUsageBlockLeave}
                            >
                              <button
                                type="button"
                                className={`cp-budget-note-block cp-budget-note-block--vert-col${
                                  en.kind === 'planned' ? ' cp-budget-note-block--planned' : ' cp-budget-note-block--done'
                                }`}
                                onClick={() => openUsageEditModal(en.id)}
                                aria-label={`집행 ${en.kind === 'planned' ? '예정' : '완료'} ${en.usage_date} ${formatMoney(Number(en.amount) || 0)} 세부보기·클릭하여 수정`}
                              >
                                <div className="cp-budget-note-block-vert-top">
                                  <span className="cp-budget-note-block-vert-date">{en.usage_date}</span>
                                  <span className="cp-budget-note-block-vert-amt">
                                    {formatMoney(Number(en.amount) || 0)}
                                  </span>
                                </div>
                                {en.memo.trim() ? (
                                  <span className="cp-budget-note-block-vert-memo">
                                    <BudgetNoteSearchHighlightedText
                                      text={en.memo.trim()}
                                      query={searchQuery}
                                      activeMatchId={activeMatchId}
                                      buildMatchId={(start) => buildBudgetUsageSearchMatchId(en.id, start)}
                                    />
                                  </span>
                                ) : null}
                                <span className="cp-budget-note-block-vert-kind-mini" aria-hidden>
                                  {en.kind === 'planned' ? '집행예정' : '집행완료'}
                                </span>
                              </button>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    </article>
                  );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSave()}
        disabled={saving || !dirty}
        aria-label="저장하기"
        title={dirty ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img src={`${process.env.PUBLIC_URL || ''}/Save.svg`} alt="" className="cp-fab-save-icon" />
      </button>
      <button
        type="button"
        className="cp-budget-note-fab"
        aria-label="예산 행 추가"
        data-tutorial-bn="fab-add-row"
        onClick={addRow}
        disabled={!userId}
      >
        +
      </button>

      {numSelSummary && numSumPopPos
        ? createPortal(
            <div className="cp-budget-note-sum-pop" role="status" aria-live="polite" style={numSumPopPos}>
              <span className="cp-budget-note-sum-pop-label">
                합계
                <small>
                  {numSelSummary.count === numSelSummary.cells
                    ? `${numSelSummary.cells}칸`
                    : `${numSelSummary.count}/${numSelSummary.cells}칸`}
                </small>
              </span>
              <span
                className={`cp-budget-note-sum-pop-value${numSelSummary.sum < 0 ? ' is-negative' : ''}`}
              >
                {formatMoney(numSelSummary.sum)}
                <span className="cp-budget-note-sum-pop-unit">원</span>
              </span>
            </div>,
            document.body
          )
        : null}

      {usageHover
        ? createPortal(
            <div
              className="cp-budget-note-fly-pop"
              role="tooltip"
              style={usageHover.style}
              onMouseEnter={onFlyPopEnter}
              onMouseLeave={onFlyPopLeave}
            >
              <BudgetNoteUsageFlyPopBody
                entry={usageHover.entry}
                searchQuery={searchQuery}
                activeMatchId={activeMatchId}
              />
            </div>,
            document.body
          )
        : null}

      {metaColsMenu
        ? createPortal(
            <div
              className="cp-budget-note-meta-cols-menu-pop"
              role="dialog"
              aria-label="예산 구분 및 내용 열 표시"
              data-tutorial-bn="meta-cols-menu"
              style={{
                position: 'fixed',
                left: metaColsMenu.left,
                top: metaColsMenu.top,
                zIndex: 92,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <p className="cp-budget-note-meta-cols-menu-title">열 보이기 · 숨기기</p>
              <div className="cp-budget-note-meta-cols-menu-list" role="group" aria-label="구분·메모 열 표시">
                {BUDGET_META_COL_KEYS.map((key) => {
                  const on = metaColsVisible[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`cp-budget-note-meta-col-toggle${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleMetaColVisible(key)}
                    >
                      <span className="cp-budget-note-meta-col-toggle-check" aria-hidden>
                        {on ? '✓' : ''}
                      </span>
                      <span className="cp-budget-note-meta-col-toggle-label">
                        {key === 'memo' ? BUDGET_META_COL_LABELS.memo : categoryHeaderLabels[key]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}

      {rowMenu
        ? createPortal(
            <div
              className="cp-budget-note-row-menu-pop"
              role="dialog"
              aria-label="행 작업"
              data-tutorial-bn="row-menu"
              style={{
                position: 'fixed',
                left: Math.max(8, Math.min(rowMenu.left, (typeof window !== 'undefined' ? window.innerWidth : 800) - 120)),
                top: rowMenu.top,
                zIndex: 92,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {(() => {
                const menuRow = rows.find((r) => r.id === rowMenu.rowId);
                const isHidden = Boolean(menuRow?.is_hidden);
                return (
                  <button
                    type="button"
                    className="cp-budget-note-hold-action-btn cp-budget-note-row-menu-hide"
                    aria-label={isHidden ? '행 숨김 해제' : '행 숨기기'}
                    title={isHidden ? '숨김 해제' : '이 행 숨기기'}
                    data-tutorial-bn="row-menu-hide"
                    onClick={() => toggleBudgetRowHidden(rowMenu.rowId)}
                  >
                    <span className="cp-budget-note-hold-action-icon" aria-hidden>
                      <img
                        src={`${pub}/${isHidden ? 'eye_open.svg' : 'eye_close.svg'}`}
                        alt=""
                        width={14}
                        height={14}
                      />
                    </span>
                  </button>
                );
              })()}
              <BudgetNoteHoldActionButton
                actionKey={`dup-${rowMenu.rowId}`}
                ariaLabel="행 복제 (길게 누르기)"
                title="길게 눌러 이 행을 바로 아래에 복제"
                className="cp-budget-note-row-menu-dup"
                data-tutorial-bn="row-menu-dup"
                onComplete={() => duplicateBudgetRow(rowMenu.rowId)}
              >
                <img src={`${pub}/Copy.svg`} alt="" width={14} height={14} />
              </BudgetNoteHoldActionButton>
              <BudgetNoteHoldActionButton
                actionKey={`del-${rowMenu.rowId}`}
                ariaLabel="행 삭제 (짧게: 확인, 길게: 바로 삭제)"
                title="짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제"
                className="cp-budget-note-row-menu-del"
                data-tutorial-bn="row-menu-del"
                onComplete={() => executeDeleteRow(rowMenu.rowId)}
                onShortPress={() => requestDeleteRow(rowMenu.rowId)}
              >
                <img src={`${pub}/Trash.svg`} alt="" width={14} height={14} className="cp-todo-note-trash-icon" />
              </BudgetNoteHoldActionButton>
            </div>,
            document.body
          )
        : null}

      {projFromCurConfirm ? (
        <div
          className="cp-budget-note-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setProjFromCurConfirm(null);
          }}
        >
          <div
            className="cp-budget-note-modal cp-budget-note-row-delete-dialog"
            role="alertdialog"
            aria-labelledby="cp-budget-proj-from-cur-title"
            aria-describedby="cp-budget-proj-from-cur-desc"
          >
            <h3 id="cp-budget-proj-from-cur-title" className="cp-budget-note-modal-title">
              사업예산에 현재예산 반영
            </h3>
            <p id="cp-budget-proj-from-cur-desc" className="cp-budget-note-row-delete-msg">
              {(() => {
                const idSet = new Set(projFromCurConfirm.rowIds);
                const changed = rows.filter(
                  (r) => idSet.has(r.id) && (Number(r.project_budget) || 0) !== (Number(r.current_budget) || 0)
                ).length;
                return `선택한 ${projFromCurConfirm.rowIds.length}개 행의 사업예산을 현재예산 금액으로 바꿉니다. (값이 바뀌는 행 ${changed}개) 계속할까요?`;
              })()}
            </p>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setProjFromCurConfirm(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => applyProjFromCur(projFromCurConfirm.rowIds)}
              >
                반영
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {suppApplyConfirm ? (
        <div
          className="cp-budget-note-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSuppApplyConfirm(false);
          }}
        >
          <div
            className="cp-budget-note-modal cp-budget-note-row-delete-dialog"
            role="alertdialog"
            aria-labelledby="cp-budget-supp-apply-title"
            aria-describedby="cp-budget-supp-apply-desc"
          >
            <h3 id="cp-budget-supp-apply-title" className="cp-budget-note-modal-title">
              {nextBudgetSuppApplyRound(rows)}차 추경 후 예산을 현재예산으로 반영
            </h3>
            <div id="cp-budget-supp-apply-desc" className="cp-budget-note-row-delete-msg cp-budget-note-supp-apply-msg">
              <p>추경 후 예산이 입력된 {suppApplyTargetRowIds.length}개 행의 현재예산을 추경 후 예산 금액으로 바꿉니다.</p>
              <ul>
                <li>추경 후 예산이 비어 있는 행은 그대로 유지됩니다.</li>
                <li>반영된 행의 추경 후 예산·추경증감 칸은 비워집니다.</li>
                <li>
                  반영된 현재예산 셀에 원래 현재예산과 {nextBudgetSuppApplyRound(rows)}차 추경 증감액이 기록되며, 셀의 표시를
                  누르면 누적 기록을 볼 수 있습니다.
                </li>
              </ul>
              <p>계속할까요?</p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setSuppApplyConfirm(false)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={applySuppToCurrent}>
                반영
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {suppHistoryRowId
        ? (() => {
            const histRow = rows.find((r) => r.id === suppHistoryRowId);
            const history = histRow?.supp_apply_history ?? [];
            const rowLabel = histRow
              ? [histRow.cat1, histRow.cat2, histRow.cat3, histRow.cat4].map((s) => s.trim()).filter(Boolean).join(' · ')
              : '';
            return (
              <div
                className="cp-budget-note-modal-overlay"
                role="presentation"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setSuppHistoryRowId(null);
                }}
              >
                <div
                  className="cp-budget-note-modal cp-budget-note-supp-history-dialog"
                  role="dialog"
                  aria-labelledby="cp-budget-supp-history-title"
                >
                  <h3 id="cp-budget-supp-history-title" className="cp-budget-note-modal-title">
                    추경 반영 기록
                  </h3>
                  {rowLabel ? <p className="cp-budget-note-supp-history-row">{rowLabel}</p> : null}
                  {history.length === 0 ? (
                    <p className="cp-budget-note-row-delete-msg">기록이 없습니다.</p>
                  ) : (
                    <ol className="cp-budget-note-supp-history-list">
                      {history.map((rec, i) => {
                        const delta = rec.after - rec.before;
                        return (
                          <li key={`${rec.round}-${i}`}>
                            <span className="cp-budget-note-supp-history-round">{rec.round}차 추경</span>
                            <span>
                              원래 현재예산 {formatMoney(rec.before)}원 →{' '}
                              <b
                                className={
                                  delta < 0
                                    ? 'cp-budget-note-supp-history-delta is-minus'
                                    : 'cp-budget-note-supp-history-delta'
                                }
                              >
                                {delta >= 0 ? '+' : '-'}
                                {formatMoney(Math.abs(delta))}원
                              </b>{' '}
                              → {formatMoney(rec.after)}원
                            </span>
                            {rec.applied_at ? (
                              <span className="cp-budget-note-supp-history-date">{rec.applied_at}</span>
                            ) : null}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <div className="cp-modal-footer">
                    <button type="button" className="cp-btn-secondary" onClick={() => setSuppHistoryRowId(null)}>
                      닫기
                    </button>
                  </div>
                </div>
              </div>
            );
          })()
        : null}

      {rowDeleteConfirm ? (
        <div
          className="cp-budget-note-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setRowDeleteConfirm(null);
          }}
        >
          <div
            className="cp-budget-note-modal cp-budget-note-row-delete-dialog"
            role="alertdialog"
            aria-labelledby="cp-budget-row-del-title"
            aria-describedby="cp-budget-row-del-desc"
          >
            <h3 id="cp-budget-row-del-title" className="cp-budget-note-modal-title">
              행 삭제
            </h3>
            <p id="cp-budget-row-del-desc" className="cp-budget-note-row-delete-msg">
              {entries.some((e) => e.row_id === rowDeleteConfirm.rowId)
                ? '이 행에 연결된 집행 내역이 있습니다. 삭제하면 예산 행과 모든 집행 내역이 함께 제거됩니다. 계속할까요?'
                : '이 예산 행을 삭제할까요? 삭제하면 되돌릴 수 없습니다.'}
            </p>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setRowDeleteConfirm(null)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={() => executeDeleteRow(rowDeleteConfirm.rowId)}>
                삭제 확인
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {usageModal ? (
        <div
          className="cp-budget-note-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeUsageModal();
          }}
        >
          <div
            className="cp-budget-note-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-budget-usage-modal-title"
            data-tutorial-bn="usage-modal"
          >
            <div className="cp-budget-note-modal-title-row">
              <h3 id="cp-budget-usage-modal-title" className="cp-budget-note-modal-title">
                {usageModal.mode === 'add' ? '집행 내역 추가' : '집행 내역 수정'}
              </h3>
              {usageModal.mode === 'edit' ? (
                <BudgetUsageDeleteHoldButton
                  entryId={usageModal.entryId}
                  onRemove={() => {
                    removeUsageEntry(usageModal.entryId);
                    closeUsageModal();
                  }}
                />
              ) : (
                <span className="cp-budget-note-modal-title-spacer" aria-hidden />
              )}
            </div>
            <div className="cp-budget-note-modal-body">
              <div className="cp-budget-note-field cp-budget-note-field--kind">
                <span id="cp-budget-usage-kind-label">구분</span>
                <div
                  className="cp-budget-note-usage-kind-tabs"
                  role="radiogroup"
                  aria-labelledby="cp-budget-usage-kind-label"
                  data-tutorial-bn="usage-kind"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={usageForm.kind === 'planned'}
                    className={`cp-annual-progress-tab cp-budget-note-usage-tab-planned${usageForm.kind === 'planned' ? ' is-active' : ''}`}
                    data-tutorial-bn="usage-kind-planned"
                    onClick={() => setUsageForm((f) => ({ ...f, kind: 'planned' }))}
                  >
                    집행예정
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={usageForm.kind === 'completed'}
                    className={`cp-annual-progress-tab cp-budget-note-usage-tab-completed${usageForm.kind === 'completed' ? ' is-active' : ''}`}
                    data-tutorial-bn="usage-kind-completed"
                    onClick={() => setUsageForm((f) => ({ ...f, kind: 'completed' }))}
                  >
                    집행완료
                  </button>
                </div>
              </div>
              <div className="cp-budget-note-field cp-budget-note-field--date" aria-label="날짜">
                <div
                  className="cp-budget-note-usage-date-embed"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <BudgetNoteUsageDateField
                    iso={usageForm.usage_date}
                    dueKind={usageForm.usage_due_kind}
                    onChange={({ iso, dueKind }) =>
                      setUsageForm((f) => ({ ...f, usage_date: iso, usage_due_kind: dueKind }))
                    }
                  />
                </div>
              </div>
              <label className="cp-budget-note-field cp-budget-note-field--amount">
                <span>금액</span>
                <span className="cp-budget-note-amount-box">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="cp-budget-note-input cp-budget-note-modal-amount-input"
                  value={usageAmountDisplayFromDigits(usageForm.amount)}
                  onChange={(e) => {
                    setUsageBalanceDraft(null);
                    setUsageForm((f) => ({ ...f, amount: usageAmountDigitsFromRaw(e.target.value) }));
                  }}
                  placeholder="0"
                />
                <span
                  className="cp-budget-note-modal-amount-hangul"
                  aria-live="polite"
                >
                  {`(${usageAmountHangulLine && usageAmountHangulLine !== '영원' ? usageAmountHangulLine : '0원'})`}
                </span>
                </span>
              </label>
              {usageBalanceBase !== null && usageExpectedBalance !== null ? (
                <label
                  className={`cp-budget-note-expected-balance${usageExpectedBalance < 0 ? ' is-negative' : ''}`}
                  title={`집행 전 잔액 ${formatMoney(usageBalanceBase)}원 − 금액 (잔액을 입력하면 금액이 맞춰집니다)`}
                >
                  <span className="cp-budget-note-expected-balance-label">예상 잔액</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="cp-budget-note-expected-balance-input"
                    size={Math.max(1, (usageBalanceDraft ?? formatMoney(usageExpectedBalance)).length)}
                    value={usageBalanceDraft ?? formatMoney(usageExpectedBalance)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setUsageBalanceDraft(usageBalanceDisplayFromRaw(raw));
                      const balance = usageBalanceValueFromRaw(raw);
                      const nextAmount = balance === null ? 0 : Math.max(0, usageBalanceBase - balance);
                      setUsageForm((f) => ({ ...f, amount: nextAmount > 0 ? String(nextAmount) : '' }));
                    }}
                    onBlur={() => setUsageBalanceDraft(null)}
                  />
                  <span className="cp-budget-note-expected-balance-unit">원</span>
                </label>
              ) : null}
              <label className="cp-budget-note-field">
                <span>메모</span>
                <textarea
                  className="cp-budget-note-textarea"
                  rows={3}
                  value={usageForm.memo}
                  onChange={(e) => setUsageForm((f) => ({ ...f, memo: e.target.value }))}
                />
              </label>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeUsageModal}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                data-tutorial-bn="usage-submit"
                onClick={submitUsageModal}
              >
                {usageModal.mode === 'add' ? '추가' : '저장'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {markColorPickModal ? (
        <ProgramColorPickModal
          type="dedicated"
          existingColors={toolbarMarkColors}
          customOnly
          showLabel={false}
          onSubmit={handleMarkColorModalSubmit}
          onClose={() => setMarkColorPickModal(false)}
        />
      ) : null}
    </section>
  );
};

export default BudgetNotePage;
